import { QueryClient, useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { $api } from '../api/queryClient'
import { apiClient } from '../api/client'
import { uploadWithProgress, documentsUploadUrl, UploadError, type UploadProgress } from '../api/upload-with-progress'
import {
  chunkedFieldsFromFormData,
  chunkedUploadFile,
  uploadDocumentChunked,
  type DocumentUploadProgress,
} from '../api/chunked-upload'
import type { components } from '../api/schema'
import { useScopeLive } from '../contexts/LiveEventsContext'
import { fetchAllPages, listQueryKey } from '../api/paginateAll'
import { parentAssetPatchBody } from '../lib/asset-parent'
import { acceptsParentType } from '../lib/asset-type-tree'

// A single shared empty-array instance for every "no data yet" / "empty list"
// case below. Returning a fresh `[]` literal each time (the previous
// behavior) gives every caller a NEW array reference on every render — any
// downstream `useMemo`/`useEffect` keyed on that value (e.g. a floor list
// derived from an empty-children building) then recomputes/re-fires on
// every single render, not just when the data actually changes. That in
// turn can wipe out unrelated component state on every re-render (see
// AddStackingEntityDialog's "reset on open" effect, which depended on such
// a derived array and cleared the in-progress name field after almost every
// keystroke — spec 2026-08-25-02).
const EMPTY_DATA_ARRAY: never[] = []

// Helper function to extract data from paginated responses
function extractData<T>(result: { data?: { data?: T[] } | T }): T[] {
  // Note: the API represents an empty list as `data: null`, not `data: []`
  // — Array.isArray(null) is false, so that case falls through to the final
  // fallback below, which is why it must also return the shared constant.
  if (!result.data) return EMPTY_DATA_ARRAY
  if (result.data && typeof result.data === 'object' && 'data' in result.data && Array.isArray(result.data.data)) {
    return result.data.data
  }
  if (Array.isArray(result.data)) {
    return result.data
  }
  return EMPTY_DATA_ARRAY
}

// Helper function to extract single item from response
function extractItem<T>(result: { data?: T }): T | undefined {
  return result.data
}

// Type definitions from the OpenAPI schema - exported for use by other components
export type Document = components['schemas']['Document']
export type Asset = components['schemas']['Asset'] & {
  archived_at?: string | null
  external_ids?: {
    code?: string
  }
  // Present when the list endpoint is called with ?include=properties.
  properties?: Array<{ uid?: string; code?: string; value?: unknown; source?: string }>
  start_date?: string
  end_date?: string
  picture?: {
    document_uid?: string
  }
  flags?: string[]
}
export type AssetDocumentFolders = components['schemas']['AssetDocumentFolders']
export type AssetDocumentFolder = components['schemas']['AssetDocumentFolder']
export type AssetPropertyValue = components['schemas']['AssetPropertyValue']
export type SetPropertyValueRequest = components['schemas']['SetPropertyValueRequest']
export type CreateAssetRequest = components['schemas']['CreateAssetRequest']
export type UpdateAssetTypeRequest = components['schemas']['UpdateAssetTypeRequest']
export type AssetType = components['schemas']['AssetType'] & {
  name: Record<string, string> | string
  possible_parent_asset_type_uids?: string[]
}
export type PropertyDefinition = components['schemas']['PropertyDefinition'] & {
  name: Record<string, string> | string
  description?: Record<string, string> | string
}
export type PropertyCategory = components['schemas']['PropertyCategory'] & {
  name: Record<string, string> | string
}
export type PropertyEnumValue = components['schemas']['PropertyEnumValue']
export type CreatePropertyDefinitionRequest = components['schemas']['CreatePropertyDefinitionRequest']
export type UpdatePropertyDefinitionRequest = Omit<components['schemas']['UpdatePropertyDefinitionRequest'], 'name' | 'description'> & {
  name: Record<string, string>
  description?: Record<string, string>
}

export interface DocumentType {
  uid: string
  code: string
  name: Record<string, string> | string
}

export interface CreatePropertyEnumValueRequest {
  value: string
  name: { [key: string]: string }
  description?: { [key: string]: string }
  order_index?: number
  properties?: { [key: string]: unknown }
}

export interface UpdatePropertyEnumValueRequest {
  value?: string
  name?: { [key: string]: string }
  description?: { [key: string]: string }
  order_index?: number
  properties?: { [key: string]: unknown }
}

export interface UserData {
  uid: string
  name?: string
  email: string
  properties?: {
    firstName?: string
    lastName?: string
    phone?: string
  }
  picture?: {
    document_uid?: string
  }
}

export interface OrganizationData {
  uid: string
  name?: string
  code: string
  /**
   * The organization's CONTENT language — the language AI summaries, generated
   * room names and resident-facing copy are written in. Resolved server-side
   * off the `locale` organization setting (spec 2026-09-09-10); NOT the UI
   * language, which is a per-user preference (LocaleContext).
   */
  locale?: 'en' | 'fr'
  /** The `jurisdiction.default` setting (FR, GB-ENG); "" when none is set (spec 2026-10-25-04). */
  jurisdiction?: string
  /** The `currency.default` setting (ISO 4217), EUR unless changed (spec 2026-10-27-02). */
  currency?: string
  picture?: {
    document_uid?: string
  }
}

// Query keys for cache management
export const queryKeys = {
  documents: (org: string, assetUid?: string, documentTypeUid?: string) => {
    const key = ['documents', org];
    if (assetUid) key.push(assetUid);
    if (documentTypeUid) key.push('type', documentTypeUid);
    return key;
  },
  document: (org: string, docUid: string) => ['document', org, docUid] as const,
  documentTypes: (org: string) => ['documentTypes', org] as const,
  documentTypeProperties: (org: string, documentTypeUid: string) => ['documentTypeProperties', org, documentTypeUid] as const,
  documentProperties: (org: string, documentUid: string) => ['documentProperties', org, documentUid] as const,
  assets: (org: string) => ['assets', org] as const,
  // The asset DETAIL and PROPERTIES queries are `$api.useQuery` calls
  // (useAsset / useAssetProperties below), so openapi-react-query owns their
  // cache key and gives them its [method, path, init] shape. A hand-rolled
  // ['asset', org, uid] key matched NOTHING, which made every
  // `invalidateQueries({ queryKey: queryKeys.asset(...) })` call site a silent
  // no-op — the asset detail then only refreshed when the dynamic-refresh poll
  // in AssetDetailPage happened to fire, which is a coin flip while the live
  // socket is connected (it pins the poll to 5 min). That is what made
  // e2e/composite-plan.spec.ts's "deleting the last registration removes the
  // tab itself" flaky: the `composite_plan` flag change never reached the tab
  // strip. `refreshAssetProperties` below had already worked around the
  // properties half of it with a hand-written predicate.
  //
  // Naming only the two path params is enough: invalidateQueries compares
  // non-exact keys with a deep PARTIAL match, so this hits the query whatever
  // `include` list its `params.query` carries.
  asset: (org: string, assetUid: string) => [
    'get',
    '/api/v1/organizations/{orgCode}/assets/{assetUid}',
    { params: { path: { orgCode: org, assetUid } } },
  ] as const,
  assetProperties: (org: string, assetUid: string) => [
    'get',
    '/api/v1/organizations/{orgCode}/assets/{assetUid}/properties',
    { params: { path: { orgCode: org, assetUid } } },
  ] as const,
  assetTypes: (org: string, assetTypeUid?: string) => ['assetTypes', org, assetTypeUid] as const,
  assetTypeProperties: (org: string, assetTypeUid: string) => ['assetTypeProperties', org, assetTypeUid] as const,
  propertyAssetTypes: (org: string, propertyUid: string) => ['propertyAssetTypes', org, propertyUid] as const,
  propertyUsage: (org: string, propertyUid: string) => ['propertyUsage', org, propertyUid] as const,
  propertyCategories: (org: string) => ['propertyCategories', org] as const,
  propertyDefinitions: (org: string, entityType?: string) => {
    const key = ['propertyDefinitions', org];
    if (entityType) key.push('entityType', entityType);
    return key;
  },
  propertyDefinition: (org: string, propertyUid: string) => ['propertyDefinition', org, propertyUid] as const,
  propertyEnumValues: (org: string, propertyUid: string) => ['propertyEnumValues', org, propertyUid] as const,
  user: (org: string, userUid: string) => ['user', org, userUid] as const,
  userProperties: (org: string, userUid: string) => ['userProperties', org, userUid] as const,
  organization: (org: string) => ['organization', org] as const,
  contractTypes: (org: string, contractTypeUid?: string) => ['contractTypes', org, contractTypeUid] as const,
  contractStakeholderTypes: (org: string, contractStakeholderTypeUid?: string) => ['contractStakeholderTypes', org, contractStakeholderTypeUid] as const,
  contracts: (org: string, contractUid?: string) => ['contracts', org, contractUid] as const,
  contractStakeholders: (org: string, contractUid: string) => ['contractStakeholders', org, contractUid] as const,
  contractAssets: (org: string, contractUid: string) => ['contractAssets', org, contractUid] as const,
}

// Documents list query hook
export function useDocuments(
  org: string,
  assetUid?: string,
  documentTypeUid?: string,
  flags?: string,
  limit?: number,
  importUid?: string,
  contractUid?: string,
  pii?: 'true' | 'false' | 'unknown',
  // Deposit drill-downs (spec 2026-08-16-07 §2). Grouped in an options object
  // rather than appended as two more positionals: this signature is already at
  // its readable limit.
  deposit?: { pathPrefix?: string; unclassified?: boolean; toValidate?: boolean }
) {
  const queryParams: Record<string, string> = { include: 'asset_uid,contract_uid,document_type_uid' }
  if (assetUid) queryParams.asset_uid = assetUid
  // The document's *home* contract (documents.contract_id, spec
  // 2026-08-13-01) — not the linked_entity_* semi-join, which matches the
  // secondary document_links cross-references.
  if (contractUid) queryParams.contract_uid = contractUid
  if (documentTypeUid) queryParams.document_type_uid = documentTypeUid
  if (flags) queryParams.flags = flags
  // Tri-state PII filter (spec 2026-08-13-02 §6), expanded server-side onto
  // the pii:* tokens. Kept separate from `flags`, which stays exact-token.
  if (pii) queryParams.pii = pii
  if (limit) queryParams.limit = String(limit)
  // Import drill-down (spec 2026-07-27-10 §7). The backend also lifts its
  // default "hide attached documents" exclusion for this filter, so the count
  // reconciles with the dashboard's funnel.
  if (importUid) queryParams.import_uid = importUid
  // Folder drill-down from the import dashboard's deposit tree, and its review
  // queue. Both are prefix/boolean filters on the import's own documents.
  if (deposit?.pathPrefix) queryParams.path_prefix = deposit.pathPrefix
  if (deposit?.unclassified) queryParams.unclassified = 'true'
  // The sidebar badge's list (spec 2026-10-27-23).
  if (deposit?.toValidate) queryParams.to_validate = 'true'

  const result = $api.useQuery('get', '/api/v1/organizations/{orgCode}/documents', {
    params: {
      path: { orgCode: org },
      query: queryParams
    }
  })

  // Pagination envelope (total/limit) from the API response, used by the
  // documents list page to distinguish "showing N of TOTAL" from "TOTAL
  // documents".
  const total = (result.data as { total?: number } | undefined)?.total

  return {
    ...result,
    data: extractData(result) as Document[],
    total
  }
}

// Document query hook
export function useDocument(org: string, docUid: string) {
  // isLive: whether the documents:docUid detail scope is subscribed AND
  // acked by the server (see LiveEventsContext.tsx's useScopeLive). While
  // true, a DocumentDetailPage mount above already gets processing-flag
  // changes pushed via WS within ~1-2s, so the LLM-fallback poll below can
  // relax to a lazy safety net instead of its normal 5s cadence — mirrors
  // AssetDetailPage's useDynamicRefresh({ live }) pattern, adapted here
  // directly since useDocument's refetchInterval isn't driven by
  // useDynamicRefresh.
  const isLive = useScopeLive({ entity: 'documents', uid: docUid })

  const result = $api.useQuery('get', '/api/v1/organizations/{orgCode}/documents/{documentUid}', {
    params: {
      path: {
        orgCode: org,
        documentUid: docUid
      }
    }
  }, {
    enabled: !!org && !!docUid,
    // Poll while the LLM-fallback classifier is working on this document so the
    // proposal / suggestion surfaces without a manual refresh. 30s while live
    // (WS updates handle real-time freshness; this is just a backstop for a
    // missed hint), 5s otherwise (no live subscription session, e.g. a page
    // not currently subscribed or the socket down).
    refetchInterval: (query: any) => {
      const doc = query?.state?.data?.data as Document | undefined
      const pf = (doc as any)?.processing_flags as string[] | undefined
      if (!pf?.includes('document_type_llm_search')) return false
      return isLive ? 30000 : 5000
    }
  })

  return {
    ...result,
    data: extractItem(result) as Document
  }
}

// Document with text content query hook
export function useDocumentWithTextContent(org: string, docUid: string, enabled: boolean = true) {
  const result = $api.useQuery('get', '/api/v1/organizations/{orgCode}/documents/{documentUid}', {
    params: {
      path: {
        orgCode: org,
        documentUid: docUid
      },
      query: {
        include: ['text_content']
      }
    }
  }, {
    enabled: !!org && !!docUid && enabled
  })

  return {
    ...result,
    data: extractItem(result) as Document & { text_content?: string }
  }
}

// Document types query hook
export function useDocumentTypes(org: string) {
  const result = $api.useQuery('get', '/api/v1/organizations/{orgCode}/document-types', {
    params: {
      path: { orgCode: org }
    }
  })

  return {
    ...result,
    data: extractData(result) as DocumentType[]
  }
}

export function useDocumentType(org: string, documentTypeUid: string) {
  const result = $api.useQuery('get', '/api/v1/organizations/{orgCode}/document-types/{documentTypeUid}', {
    params: {
      path: {
        orgCode: org,
        documentTypeUid: documentTypeUid
      }
    }
  })

  return {
    ...result,
    data: extractItem(result) as DocumentType
  }
}

// Assets query hook
export function useAssets(org: string, assetTypeUids?: string | string[]) {
  const queryParams: Record<string, string> = {}

  // Convert array of UIDs to comma-separated string for the API
  const assetTypeParam = Array.isArray(assetTypeUids)
    ? assetTypeUids.filter(uid => uid).join(',')
    : assetTypeUids

  if (assetTypeParam) {
    queryParams.asset_type_uid = assetTypeParam
  }

  const result = $api.useQuery('get', '/api/v1/organizations/{orgCode}/assets', {
    params: {
      path: { orgCode: org },
      query: Object.keys(queryParams).length > 0 ? queryParams : undefined
    }
  })

  return {
    ...result,
    data: extractData(result) as Asset[]
  }
}

// Assets search query hook
export function useSearchAssets(org: string, query: string) {
  const result = $api.useQuery('get', '/api/v1/organizations/{orgCode}/assets', {
    params: {
      path: { orgCode: org },
      query: {
        q: query,
        limit: 50,
        sort: '-updated_at'
      }
    }
  }, {
    enabled: !!org && !!query && query.length >= 2,
    staleTime: 30 * 1000
  })

  return {
    ...result,
    data: extractData(result) as Asset[]
  }
}

// Assets by specific UIDs query hook
export function useAssetsByUids(org: string, assetUids?: string[]) {
  const filteredUids = assetUids?.filter(uid => uid)

  const result = $api.useQuery('get', '/api/v1/organizations/{orgCode}/assets', {
    params: {
      path: { orgCode: org },
      query: filteredUids && filteredUids.length > 0 ? { uid: filteredUids } : undefined
    }
  }, {
    enabled: !!org && !!assetUids && assetUids.length > 0
  })

  return {
    ...result,
    data: extractData(result) as Asset[]
  }
}

// Assets filtered by a single property value (e.g. duplicate-address detection
// in the create wizard). Uses the `property.<code>=<value>` query convention on
// the assets list endpoint. The dynamic key is not expressible in the generated
// query type, so the query object is cast to `any` (openapi-fetch still
// serializes every key it is given).
export function useAssetsByProperty(
  org: string,
  params: { assetTypeCode?: string; propertyCode: string; value?: string | null },
  options?: { enabled?: boolean }
) {
  const { assetTypeCode, propertyCode, value } = params
  const query: Record<string, string> = { limit: '5' }
  if (assetTypeCode) query.asset_type_code = assetTypeCode
  if (value) query[`property.${propertyCode}`] = value

  const result = $api.useQuery('get', '/api/v1/organizations/{orgCode}/assets', {
    params: {
      path: { orgCode: org },
      query,
    }
  }, {
    enabled: (options?.enabled ?? true) && !!org && !!value,
    staleTime: 30 * 1000,
  })

  return {
    ...result,
    data: extractData(result) as Asset[]
  }
}

// Asset types query hook
export function useAssetTypes(org: string, includePossibleParents?: boolean) {
  const queryParams: Record<string, string> = {}
  if (includePossibleParents) {
    queryParams.include = 'possible_parent_asset_type_uids'
  }

  const result = $api.useQuery('get', '/api/v1/organizations/{orgCode}/asset-types', {
    params: {
      path: { orgCode: org },
      query: { limit: 200, ...(Object.keys(queryParams).length > 0 ? queryParams : {}) }
    }
  })

  return {
    ...result,
    data: extractData(result) as unknown as AssetType[]
  }
}

// Country-scoped base-property socle for an asset (Data tab). Returns the
// expected properties for the asset's type + country, grouped by category,
// each with its requirement and expected_source hint.
export function useAssetBaseProperties(org: string, assetUid: string) {
  return $api.useQuery('get', '/api/v1/organizations/{orgCode}/assets/{assetUid}/base-properties', {
    params: {
      path: { orgCode: org, assetUid },
    },
  }, {
    enabled: !!org && !!assetUid,
    staleTime: 5 * 60 * 1000,
  })
}

// Hook to get a single asset type by code, with caching
export function useAssetTypeByCode(org: string, code: string) {
  const result = $api.useQuery('get', '/api/v1/organizations/{orgCode}/asset-types', {
    params: {
      path: { orgCode: org },
      query: { q: code, limit: 10 }
    }
  }, {
    enabled: !!code,
    staleTime: 5 * 60 * 1000, // cache for 5 minutes — asset types rarely change
  })

  const allMatches = extractData(result) as unknown as AssetType[]
  const exactMatch = allMatches?.find((t: AssetType) => t.code === code)

  return {
    ...result,
    data: exactMatch,
  }
}

/** An archived asset type takes no new asset (spec 2026-10-11-28): pickers skip it. */
export function withoutArchivedTypes<T extends { archived_at?: string | null; uid?: string }>(types: T[], keepUid?: string | null): T[] {
  return types.filter((at) => !at.archived_at || (!!keepUid && at.uid === keepUid))
}

// Hook to get asset types filtered by what can be children of a specific parent asset
export function useChildAssetTypes(org: string, parentAssetUid?: string) {
  // Resolve the parent asset by a direct fetch-by-UID rather than searching the
  // paginated `useAssets(org)` list (which caps at 20 rows — parents outside the
  // first page were never found, wrongly returning ALL asset types as allowed
  // children). React Query dedupes this with AssetCreatePage's own useAsset call.
  const { data: parentAsset, isLoading: parentLoading } = useAsset(org, parentAssetUid || '')
  const { data: allAssetTypesWithParents = [], isLoading: assetTypesLoading } = useAssetTypes(org, true)

  // No parent specified → offer every asset type (used by the no-parent create flow).
  if (!parentAssetUid) {
    return {
      data: withoutArchivedTypes(allAssetTypesWithParents),
      isLoading: assetTypesLoading,
      parentAssetTypeUid: undefined as string | undefined
    }
  }

  // Parent specified but not yet resolved → no candidates yet. Never fall back to
  // all types here: doing so would flip AssetCreatePage into the equipment-only
  // picker for parents (e.g. Building Groups) whose real children are non-equipment.
  const parentAssetTypeUid = parentAsset?.asset_type_uid
  if (!parentAssetTypeUid) {
    return {
      data: [] as AssetType[],
      isLoading: parentLoading || assetTypesLoading,
      parentAssetTypeUid: undefined as string | undefined
    }
  }

  // Parent resolved → only asset types that explicitly list the parent's asset
  // type as a possible parent.
  // A rule written against a type covers its subtypes too: equipment that
  // accepts `unit` is offered under a `unit_housing` (spec 2026-10-09-04), the
  // same parent-side walk the API's assetTypeAcceptsParent does.
  const byUid = new Map(allAssetTypesWithParents.map((at: AssetType) => [at.uid, at]))
  const filteredAssetTypes = withoutArchivedTypes(allAssetTypesWithParents).filter((assetType: AssetType) =>
    acceptsParentType(assetType, parentAssetTypeUid, byUid))

  return {
    data: filteredAssetTypes,
    isLoading: parentLoading || assetTypesLoading,
    parentAssetTypeUid
  }
}

// Hook to get child assets for a specific parent asset. Always in_tree=true:
// every current caller (right panel, stacking plan, worksite location
// pickers, compliance floor/unit counts) means "the physical/spatial
// children of this asset" and none of them want equipment mixed in (spec
// 2026-07-24-02-asset-type-in-tree-flag).
export function useChildAssets(org: string, parentAssetUid?: string, enabled = true, withProperties = false) {
  const result = $api.useQuery('get', '/api/v1/organizations/{orgCode}/assets', {
    params: {
      path: { orgCode: org },
      query: {
        parent_asset_uid: parentAssetUid!,
        sort: 'name',
        in_tree: true,
        ...(withProperties ? { include: ['properties'] } : {})
      }
    }
  }, {
    enabled: !!org && !!parentAssetUid && enabled
  })

  return {
    ...result,
    data: extractData(result) as Asset[]
  }
}

// Asset type update mutation hook
export function useUpdateAssetType(org: string, assetTypeUid: string) {
  const queryClient = useQueryClient()
  const baseMutation = $api.useMutation('patch', '/api/v1/organizations/{orgCode}/asset-types/{assetTypeUid}', {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.assetTypes(org) })
      queryClient.invalidateQueries({ queryKey: queryKeys.assetTypes(org, assetTypeUid) })
      // openapi-fetch query keys (what the queries actually use)
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey as unknown[]
          if (queryKey.length >= 2 && queryKey[0] === 'get') {
            const path = queryKey[1]
            if (path === '/api/v1/organizations/{orgCode}/asset-types' ||
                path === '/api/v1/organizations/{orgCode}/asset-types/{assetTypeUid}') {
              const params = (queryKey.find(item =>
                item && typeof item === 'object' && 'params' in (item as object)
              ) as { params?: { path?: { orgCode?: string } } } | undefined)?.params
              if (params?.path?.orgCode === org) return true
            }
          }
          return false
        }
      })
    }
  })
  return {
    ...baseMutation,
    mutate: (data: UpdateAssetTypeRequest, options?: any) => {
      baseMutation.mutate({
        params: { path: { orgCode: org, assetTypeUid } },
        body: data
      }, options)
    },
    mutateAsync: (data: UpdateAssetTypeRequest) => {
      return baseMutation.mutateAsync({
        params: { path: { orgCode: org, assetTypeUid } },
        body: data
      })
    }
  }
}

export function refreshDocument(queryClient: QueryClient, org: string, docUid: string) {
  queryClient.invalidateQueries({ queryKey: queryKeys.document(org, docUid) })
  queryClient.invalidateQueries({
    predicate: query => {
      const [method, path] = query.queryKey
      return method === 'get' && path === '/api/v1/organizations/{orgCode}/documents/{documentUid}'
    }
  })
  queryClient.invalidateQueries({ queryKey: queryKeys.documents(org) })
  queryClient.invalidateQueries({
    predicate: query => {
      const [method, path] = query.queryKey
      return method === 'get' && path === '/api/v1/organizations/{orgCode}/documents'
    }
  })
}

// Document update mutation hook
export function useUpdateDocument(org: string, docUid: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation('patch', '/api/v1/organizations/{orgCode}/documents/{documentUid}', {
    onSuccess: () => {
      refreshDocument(queryClient, org, docUid)
    }
  })

  return {
    ...baseMutation,
    mutate: (data: any, options?: any) => {
      baseMutation.mutate({
        params: { path: { orgCode: org, documentUid: docUid } },
        body: data
      }, options)
    },
    mutateAsync: (data: any, options?: any) => {
      return baseMutation.mutateAsync({
        params: { path: { orgCode: org, documentUid: docUid } },
        body: data
      }, options)
    }
  }
}

// Document delete mutation hook
export function useDeleteDocument(org: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation('delete', '/api/v1/organizations/{orgCode}/documents/{documentUid}', {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.documents(org) })
      queryClient.invalidateQueries({
        predicate: query => {
          const [method, path] = query.queryKey
          return method === 'get' && path === '/api/v1/organizations/{orgCode}/documents'
        }
      })
    }
  })

  return {
    ...baseMutation,
    mutate: (documentUid: string, options?: any) => {
      baseMutation.mutate({
        params: { path: { orgCode: org, documentUid } }
      }, options)
    },
    mutateAsync: (documentUid: string, options?: any) => {
      return baseMutation.mutateAsync({
        params: { path: { orgCode: org, documentUid } }
      }, options)
    }
  }
}

export function useRefreshDocuments(org: string) {
  const queryClient = useQueryClient()
  queryClient.invalidateQueries({ queryKey: queryKeys.documents(org) })
  queryClient.invalidateQueries({
    predicate: query => {
      const [method, path] = query.queryKey
      return method === 'get' && path === '/api/v1/organizations/{orgCode}/documents'
    }
  })
}

export type BulkDocumentsBody = {
  document_uids: string[]
  action: 'delete' | 'update'
  update?: {
    document_type_uid?: string | null
    asset_uid?: string | null
  }
}

export type BulkDocumentsResult = {
  updated: number
  skipped: string[]
}

// Bulk delete/update of a batch of documents in a single request. Mirrors the
// invalidation used by useDeleteDocument so the list and any document queries
// refresh after the batch operation.
export function useBulkDocuments(org: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation('post', '/api/v1/organizations/{orgCode}/documents/bulk', {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.documents(org) })
      queryClient.invalidateQueries({
        predicate: query => {
          const [method, path] = query.queryKey
          return method === 'get' && path === '/api/v1/organizations/{orgCode}/documents'
        }
      })
    }
  })

  return {
    ...baseMutation,
    mutate: (body: BulkDocumentsBody, options?: any) => {
      baseMutation.mutate({
        params: { path: { orgCode: org } },
        body: body as any
      }, options)
    },
    mutateAsync: (body: BulkDocumentsBody, options?: any) => {
      return baseMutation.mutateAsync({
        params: { path: { orgCode: org } },
        body: body as any
      }, options)
    }
  }
}

/**
 * Document upload mutation hook.
 *
 * `importUid` (spec 2026-08-16-05 §2) optionally binds every upload of this
 * surface to an open import session: it is sent as the `X-Import-Uid` header,
 * which the org-wide import middleware turns into
 * `source_details.import_uid` on the created document. Absent/empty ⇒ the
 * header is not sent at all, so an ungrouped upload behaves exactly as before.
 */
export function useUploadDocument(org: string, importUid?: string) {
  const queryClient = useQueryClient()
  const importHeaders = importUid ? { 'X-Import-Uid': importUid } : undefined

  const invalidateDocuments = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.documents(org) })
    queryClient.invalidateQueries({
      predicate: query => {
        const [method, path] = query.queryKey
        return method === 'get' && path === '/api/v1/organizations/{orgCode}/documents'
      }
    })
    queryClient.invalidateQueries({
      predicate: query => {
        const [method, path] = query.queryKey
        return method === 'get' && path === '/api/v1/organizations/{orgCode}/assets/{assetUid}'
      }
    })
  }

  const baseMutation = $api.useMutation('post', '/api/v1/organizations/{orgCode}/documents', {
    onSuccess: invalidateDocuments
  })

  // XMLHttpRequest transport, used only when the caller asks for progress
  // (fetch cannot report upload progress). Same headers, same invalidations.
  const progressMutation = useMutation({
    mutationFn: ({ data, onProgress }: { data: FormData; onProgress: (p: UploadProgress) => void }) =>
      uploadWithProgress({
        url: documentsUploadUrl(org),
        formData: data,
        headers: importHeaders,
        onProgress
      }),
    onSuccess: invalidateDocuments
  })

  // Files over 32 MB go up in chunks assembled by a server job (spec
  // 2026-10-02-03), then resolve with the created document like POST
  // /documents does. A failure rejects with an `UploadError` or, when the
  // assembly failed, a `ChunkedUploadError`.
  const chunkedMutation = useMutation({
    mutationFn: async ({ file, data, onProgress }: { file: File; data: FormData; onProgress?: (p: DocumentUploadProgress) => void }) => {
      const { document_uid } = await uploadDocumentChunked(org, file, chunkedFieldsFromFormData(data), onProgress, {
        headers: importHeaders,
      })
      const { data: doc, error, response } = await apiClient.GET('/api/v1/organizations/{orgCode}/documents/{documentUid}', {
        params: { path: { orgCode: org, documentUid: document_uid } },
      })
      if (!doc) throw new UploadError(response.status, error)
      return doc
    },
    onSuccess: invalidateDocuments
  })

  return {
    ...baseMutation,
    isPending: baseMutation.isPending || progressMutation.isPending || chunkedMutation.isPending,
    mutate: (data: FormData, options?: any) => {
      const file = chunkedUploadFile(data)
      if (file) {
        const { onProgress, ...rest } = options ?? {}
        chunkedMutation.mutate({ file, data, onProgress }, rest)
        return
      }
      baseMutation.mutate({
        params: { path: { orgCode: org } },
        headers: importHeaders,
        body: data as any
      }, options)
    },
    /**
     * `options.onProgress` switches to the XHR transport and reports
     * `{loaded,total}` (plus `phase: 'assembling'` on the chunked path); a
     * failure then rejects with an `UploadError`.
     */
    mutateAsync: (data: FormData, options?: any) => {
      const file = chunkedUploadFile(data)
      if (file) {
        const { onProgress, ...rest } = options ?? {}
        return chunkedMutation.mutateAsync({ file, data, onProgress }, rest)
      }
      if (options?.onProgress) {
        const { onProgress, ...rest } = options
        return progressMutation.mutateAsync({ data, onProgress }, rest)
      }
      return baseMutation.mutateAsync({
        params: { path: { orgCode: org } },
        headers: importHeaders,
        body: data as any
      }, options)
    }
  }
}

// Asset update mutation hook
export function useUpdateAsset(org: string, assetUid: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation('patch', '/api/v1/organizations/{orgCode}/assets/{assetUid}', {
    onSuccess: () => {
      refreshAsset(queryClient, org, assetUid)
    }
  })

  return {
    ...baseMutation,
    mutate: (data: any, options?: any) => {
      baseMutation.mutate({
        params: { path: { orgCode: org, assetUid } },
        body: data
      }, options)
    },
    mutateAsync: (data: any, options?: any) => {
      return baseMutation.mutateAsync({
        params: { path: { orgCode: org, assetUid } },
        body: data
      }, options)
    }
  }
}

/**
 * Move ANY asset under a new parent (or to the root with `null`).
 *
 * `useUpdateAsset` binds one asset uid at hook time, which a tree's drag &
 * drop cannot do — the asset being moved is whichever row the user picked up.
 * The body is `parentAssetPatchBody`'s, so this shares the identity row's and
 * the Move dialog's single rule (spec 2026-09-18-01).
 */
export function useMoveAsset(org: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation('patch', '/api/v1/organizations/{orgCode}/assets/{assetUid}')

  return {
    ...baseMutation,
    moveAsync: async (assetUid: string, parentUid: string | null) => {
      const result = await baseMutation.mutateAsync({
        params: { path: { orgCode: org, assetUid } },
        body: parentAssetPatchBody(parentUid) as never,
      })
      // Both ends of the move change: the asset itself, and the children
      // listings of the parent it left and the one it joined.
      refreshAsset(queryClient, org, assetUid)
      queryClient.invalidateQueries({
        predicate: query => {
          const [method, path] = query.queryKey
          return method === 'get' && path === '/api/v1/organizations/{orgCode}/assets'
        }
      })

      return result
    },
  }
}

function refreshAsset(queryClient: QueryClient, org: string, assetUid: string | undefined) {
  if (assetUid) {
    queryClient.invalidateQueries({ queryKey: queryKeys.asset(org, assetUid) })
  }
  queryClient.invalidateQueries({
    predicate: query => {
      const [method, path] = query.queryKey
      return method === 'get' && path === '/api/v1/organizations/{orgCode}/assets/{assetUid}'
    }
  })
  queryClient.invalidateQueries({ queryKey: queryKeys.assets(org) })
  queryClient.invalidateQueries({
    predicate: query => {
      const [method, path] = query.queryKey
      return method === 'get' && path === '/api/v1/organizations/{orgCode}/assets'
    }
  })
}

export function useDeleteAsset(org: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation('delete', '/api/v1/organizations/{orgCode}/assets/{assetUid}', {
    onSuccess: () => {
      refreshAsset(queryClient, org, undefined)
    }
  })

  return {
    ...baseMutation,
    mutateAsync: (vars: {
      assetUid: string
      childrenStrategy?: 'cascade' | 'migrate'
      migrateTo?: string
    }, options?: any) => {
      return baseMutation.mutateAsync({
        params: {
          path: { orgCode: org, assetUid: vars.assetUid },
          query: {
            children_strategy: vars.childrenStrategy,
            migrate_to: vars.migrateTo,
          },
        },
      }, options)
    }
  }
}

export function useArchiveAsset(org: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation('post', '/api/v1/organizations/{orgCode}/assets/{assetUid}/archive', {
    onSuccess: () => {
      refreshAsset(queryClient, org, undefined)
    }
  })

  return {
    ...baseMutation,
    mutateAsync: (vars: {
      assetUid: string
      reason?: string
    }, options?: any) => {
      return baseMutation.mutateAsync({
        params: {
          path: { orgCode: org, assetUid: vars.assetUid },
        },
        body: vars.reason ? { reason: vars.reason } : undefined,
      }, options)
    }
  }
}

export function useRestoreAsset(org: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation('post', '/api/v1/organizations/{orgCode}/assets/{assetUid}/restore', {
    onSuccess: () => {
      refreshAsset(queryClient, org, undefined)
    }
  })

  return {
    ...baseMutation,
    mutateAsync: (vars: {
      assetUid: string
    }, options?: any) => {
      return baseMutation.mutateAsync({
        params: {
          path: { orgCode: org, assetUid: vars.assetUid },
        },
      }, options)
    }
  }
}

/**
 * Approve or reject asset proposals (assets carrying the `to_approve` flag).
 *
 * Wraps `POST /assets/approve`, which is a *batch* endpoint that silently
 * routes any UID it will not act on into `skipped` (unknown to the org, or no
 * longer `to_approve`). A 200 is therefore not proof of success: callers must
 * inspect the result — see AssetApprovalBanner, which treats
 * `updated === 0 && skipped.includes(uid)` as the "someone else already
 * resolved this" race and reports it as a failure (spec 2026-07-30-02 §2).
 *
 * On success it invalidates the asset (its flags changed) and the org issues
 * list (the auto-created approval issue closes on the next sync).
 */
export function useApproveAssets(org: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation('post', '/api/v1/organizations/{orgCode}/assets/approve', {
    onSuccess: (_data: unknown, variables: any) => {
      // The asset rows themselves: their flags (and approved_at / approver)
      // changed, so the detail query and every asset list must refetch.
      const uids: string[] = variables?.body?.uids ?? []
      for (const uid of uids) {
        queryClient.invalidateQueries({ queryKey: queryKeys.asset(org, uid) })
      }
      refreshAsset(queryClient, org, undefined)
      // The auto-created `approval` issue for this asset closes on the next
      // sync — refresh the lists, the detail *and* the stats query that feeds
      // the sidebar count badge.
      refreshIssues(queryClient, org)
    }
  })

  return {
    ...baseMutation,
    mutateAsync: (vars: {
      uids: string[]
      action: 'approve' | 'reject'
      reason?: string
    }, options?: any) => {
      return baseMutation.mutateAsync({
        params: { path: { orgCode: org } },
        body: {
          uids: vars.uids,
          action: vars.action,
          ...(vars.reason ? { reason: vars.reason } : {}),
        } as never,
      }, options)
    }
  }
}

// Asset query hook
export function useAsset(org: string, assetUid: string) {
  // Check for missing parameters
  const hasValidParams = !!org && !!assetUid

  // No `queryKey` override here: openapi-react-query derives the key from
  // (method, path, init) and only honours a `queryKey` passed in its OPTIONS
  // argument — which its types forbid. One passed in `init`, as this call used
  // to, is swallowed by `InitWithUnknowns` without a type error and ends up as
  // dead weight inside the generated key. `queryKeys.asset()` mirrors the
  // generated shape instead.
  const result = $api.useQuery('get', '/api/v1/organizations/{orgCode}/assets/{assetUid}', {
    params: {
      path: {
        orgCode: org,
        assetUid: assetUid
      },
      query: {
        // `source` costs one extra `sources` lookup per asset (plus, when the
        // provenance names one, a job/approver row) and is what makes the
        // pending-approval banner able to say where the asset came from and who
        // resolved it — spec 2026-07-30-02 §1.
        include: ['external_ids', 'picture', 'flags', 'parent_chain', 'frontend_data', 'children_count', 'source']
      }
    }
  }, {
    enabled: hasValidParams
  })

  return {
    ...result,
    data: extractItem(result) as Asset,
    hasValidParams,
    missingParams: {
      org: !org,
      assetUid: !assetUid
    }
  }
}

// Habitability verdict of every lot under an asset, one call (the stacking
// plan reads it instead of the derived habitability properties).
export function useHabitability(org: string, assetUid: string) {
  const result = $api.useQuery('get', '/api/v1/organizations/{orgCode}/assets/{assetUid}/habitability', {
    params: { path: { orgCode: org, assetUid } },
  }, {
    enabled: !!org && !!assetUid,
  })
  return { ...result, data: result.data as components['schemas']['HabitabilityResult'] | undefined }
}

// Asset properties query hook — returns all values including pending-approval ones.
// Consumers split confirmed vs pending by checking flags.
export function useAssetProperties(org: string, assetUid: string) {
  const hasValidParams = !!org && !!assetUid
  const result = $api.useQuery('get', '/api/v1/organizations/{orgCode}/assets/{assetUid}/properties', {
    params: {
      path: {
        orgCode: org,
        assetUid: assetUid
      },
      query: { include_pending: 'true', include: 'flags' } as any,
    }
  }, {
    enabled: hasValidParams
  })

  return {
    ...result,
    data: extractData(result) as AssetPropertyValue[]
  }
}

/** Check whether a property value is pending approval (not yet confirmed). */
export function isPendingApprovalValue(p: AssetPropertyValue): boolean {
  return p.flags?.includes('to_approve') || false
}

export type AssetPropertyHistoryEntry = components['schemas']['AssetPropertyHistoryEntry']

// Asset property value history hook — returns every row (including soft-deleted)
// for a single (asset, property definition) pair, newest-first. Powers the
// property value history drawer.
export function useAssetPropertyHistory(org: string, assetUid: string, propertyUid: string | undefined) {
  const hasValidParams = !!org && !!assetUid && !!propertyUid
  const result = $api.useQuery('get', '/api/v1/organizations/{orgCode}/assets/{assetUid}/properties/{propertyUid}/history', {
    params: {
      path: {
        orgCode: org,
        assetUid: assetUid,
        propertyUid: propertyUid ?? ''
      }
    }
  }, {
    enabled: hasValidParams
  })

  return {
    ...result,
    data: extractData(result) as AssetPropertyHistoryEntry[]
  }
}

// Users search hook for ApiSearchableSelect
export interface UseUsersSearchOptions {
  org: string
  userType?: 'contact' | 'user' | 'user_group' | 'company'
  limit?: number
}

export function useUsersSearch({ org, userType, limit = 20 }: UseUsersSearchOptions) {
  // This function will perform the search. An empty query issues the request
  // with no `q` param (first page, API's default order) instead of
  // short-circuiting to [] — otherwise a freshly-opened picker would render
  // blank until the user types at least 2 characters.
  const searchUsers = async (query: string): Promise<{ value: string; label: string; data?: Record<string, unknown> }[]> => {
    try {
      const result = await apiClient.GET('/api/v1/organizations/{orgCode}/users', {
        params: {
          path: { orgCode: org },
          query: {
            ...(query ? { q: query } : {}),
            limit,
            ...(userType && { type: userType })
          }
        }
      })

      if (!result.data) {
        console.error('Failed to search users: No data returned')
        return []
      }

      const users = (result.data as any).data || []

      return users.map((user: any) => ({
        value: user.uid,
        label: user.name
          ? (user.email ? `${user.name} · ${user.email}` : user.name)
          : (user.email || user.code || user.uid),
        // Structured fields so consumers (e.g. UserSelect's onOptionSelect)
        // can capture name/email at selection time without re-parsing the
        // combined label.
        data: { name: user.name ?? null, email: user.email ?? null }
      }))
    } catch (error) {
      console.error('Failed to search for users:', error)
      return []
    }
  }

  return {
    searchUsers
  }
}

// Contacts search hook - searches users, optionally filtered by type
export function useContactsSearch(org: string, limit: number = 20, userType?: 'contact' | 'user' | 'user_group' | 'company') {
  const { searchUsers } = useUsersSearch({
    org,
    userType,
    limit
  })

  return { searchUsers }
}

// User query hook
export function useUser(org: string, userUid: string, options?: { enabled?: boolean }) {
  const result = $api.useQuery('get', '/api/v1/organizations/{orgCode}/users/{userUid}', {
    params: {
      path: {
        orgCode: org,
        userUid: userUid
      },
      query: { include: ['picture'] }
    }
  }, {
    enabled: options?.enabled !== false && !!org && !!userUid
  })

  return {
    ...result,
    data: extractItem(result) as UserData
  }
}

// User update mutation hook
// The caller's own record, through GET /users/me (spec 2026-10-23-05): gated on
// `organization.read`, so a member without `contacts.read` can load it.
export function useOwnUser(org: string, options?: { enabled?: boolean }) {
  const result = $api.useQuery('get', '/api/v1/organizations/{orgCode}/users/me', {
    params: { path: { orgCode: org }, query: { include: ['picture'] } }
  }, {
    enabled: options?.enabled !== false && !!org
  })

  return {
    ...result,
    data: extractItem(result) as UserData
  }
}

// Own name and self-owned properties (firstName, lastName, language, phone,
// dateOfBirth, placeOfBirth) through PATCH /users/me, on `organization.read`.
// Properties are merged server-side; null removes a key.
export function useUpdateOwnProfile(org: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation('patch', '/api/v1/organizations/{orgCode}/users/me', {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['get', '/api/v1/organizations/{orgCode}/users/me'] })
    }
  })

  return {
    ...baseMutation,
    mutateAsync: (body: components['schemas']['UpdateOwnProfileRequest']) => {
      return baseMutation.mutateAsync({ params: { path: { orgCode: org } }, body })
    }
  }
}

export function useUpdateUser(org: string, userUid: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation('patch', '/api/v1/organizations/{orgCode}/users/{userUid}', {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.user(org, userUid) })
    }
  })

  return {
    ...baseMutation,
    mutate: (data: any, options?: any) => {
      baseMutation.mutate({
        params: { path: { orgCode: org, userUid } },
        body: data
      }, options)
    },
    mutateAsync: (data: any, options?: any) => {
      return baseMutation.mutateAsync({
        params: { path: { orgCode: org, userUid } },
        body: data
      }, options)
    }
  }
}

// Clause analysis: the contract held to the expectations its type declares.
//
// Not generated from the OpenAPI client: the endpoint returns a computed
// verdict set rather than a stored resource, so it is fetched through the same
// authenticated client with hand-written types — the shape below mirrors
// internal/handlers/properties/clause_analysis.go.
export type ClauseVerdict = 'conform' | 'absent' | 'weaker' | 'forbidden' | 'indeterminate'

export interface ClauseFinding {
  property_uid: string
  property_code: string
  property_name?: Record<string, string>
  family?: string
  display_order: number
  verdict: ClauseVerdict
  requirement: string
  severity: number
  comparator?: string
  expected?: unknown
  actual?: unknown
  rationale?: Record<string, string>
  source_document_uid?: string
  approved: boolean
  // On an enum clause, the name of each code per locale, keyed by code.
  enum_labels?: Record<string, Record<string, string>>
}

export interface ClauseAnalysis {
  contract_uid: string
  score: number
  counts: Record<string, number>
  findings: ClauseFinding[]
}

export function useContractClauseAnalysis(
  org: string,
  contractUid: string,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: ['contract-clause-analysis', org, contractUid],
    queryFn: async () => {
      const response = await apiClient.GET(
        '/api/v1/organizations/{orgCode}/contracts/{uid}/clause-analysis' as never,
        { params: { path: { orgCode: org, uid: contractUid } } } as never,
      )
      if ((response as { error?: unknown }).error) {
        // Surface the failure to the caller: the Improvement tab must be able to
        // tell "no gap" from "could not be computed".
        throw new Error('clause analysis request failed')
      }
      return (response as { data?: ClauseAnalysis }).data as ClauseAnalysis
    },
    enabled: options?.enabled !== false && !!org && !!contractUid,
  })
}

// User properties query hook
export function useUserProperties(org: string, userUid: string) {
  return useQuery({
    queryKey: queryKeys.userProperties(org, userUid),
    queryFn: async () => {
      const response = await apiClient.GET(
        '/api/v1/organizations/{orgCode}/users/{userUid}/properties',
        { params: { path: { orgCode: org, userUid } } }
      )
      return (response.data as any)?.properties as Record<string, any> || {}
    },
    enabled: !!org && !!userUid
  })
}

// Set user property value mutation hook (PUT)
export function useSetUserPropertyValue(org: string, userUid: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ propertyCode, value }: { propertyCode: string, value: any }) => {
      return apiClient.PUT(
        '/api/v1/organizations/{orgCode}/users/{userUid}/properties/{propertyCode}',
        {
          params: { path: { orgCode: org, userUid, propertyCode } },
          body: { value }
        }
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.userProperties(org, userUid) })
      queryClient.invalidateQueries({ queryKey: queryKeys.user(org, userUid) })
    }
  })
}

// Update user property value mutation hook (PATCH)
export function useUpdateUserPropertyValue(org: string, userUid: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ propertyCode, value }: { propertyCode: string, value: any }) => {
      return apiClient.PATCH(
        '/api/v1/organizations/{orgCode}/users/{userUid}/properties/{propertyCode}',
        {
          params: { path: { orgCode: org, userUid, propertyCode } },
          body: { value }
        }
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.userProperties(org, userUid) })
      queryClient.invalidateQueries({ queryKey: queryKeys.user(org, userUid) })
    }
  })
}

// Delete user property value mutation hook
export function useDeleteUserPropertyValue(org: string, userUid: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (propertyCode: string) => {
      return apiClient.DELETE(
        '/api/v1/organizations/{orgCode}/users/{userUid}/properties/{propertyCode}',
        { params: { path: { orgCode: org, userUid, propertyCode } } }
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.userProperties(org, userUid) })
      queryClient.invalidateQueries({ queryKey: queryKeys.user(org, userUid) })
    }
  })
}

// Set asset property value mutation hook
// The single-asset property hooks send sync=true: the asset's own derived
// values are recomputed in the save's transaction, so the refetch that
// follows shows them (spec 2026-10-06-16). Bulk edits and imports stay async.
export function useSetAssetPropertyValue(org: string, assetUid: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation('post', '/api/v1/organizations/{orgCode}/assets/{assetUid}/properties', {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.assetProperties(org, assetUid) })
      queryClient.invalidateQueries({ queryKey: queryKeys.asset(org, assetUid) })
      refreshAssetProperties(queryClient, org, assetUid)
    }
  })

  return {
    ...baseMutation,
    mutate: (data: any, options?: any) => {
      baseMutation.mutate({
        params: { path: { orgCode: org, assetUid }, query: { sync: true } },
        body: data
      }, options)
    },
    mutateAsync: (data: any, options?: any) => {
      return baseMutation.mutateAsync({
        params: { path: { orgCode: org, assetUid }, query: { sync: true } },
        body: data
      }, options)
    }
  }
}

export function refreshAssetProperties(queryClient: QueryClient, org: string, assetUid: string) {
  queryClient.invalidateQueries({ queryKey: queryKeys.assetProperties(org, assetUid) })
  queryClient.invalidateQueries({
    predicate: query => {
      // Check if this is an asset properties query for the same asset
      const queryKey = query.queryKey as any[]
      if (queryKey.length >= 2 && queryKey[0] === 'get') {
        const path = queryKey[1]

        // Match the exact endpoint path
        if (path === '/api/v1/organizations/{orgCode}/assets/{assetUid}/properties') {
          // Check if the params match our org and assetUid
          const params = queryKey[2]?.params as { path: { orgCode: string, assetUid: string } }
          if (params?.path?.orgCode === org && params?.path?.assetUid === assetUid) {
            return true
          }
        }
      }
      return false
    }
  })
}

// refreshIssues invalidates every issue list / detail / stats query so that
// auto-managed issues (e.g. "Properties pending approval") update their
// count and close themselves once the underlying signal is gone. Called
// after approving or rejecting pending property values.
export function refreshIssues(queryClient: QueryClient, org: string) {
  queryClient.invalidateQueries({
    predicate: query => {
      const queryKey = query.queryKey as any[]
      if (queryKey.length < 2 || queryKey[0] !== 'get') return false
      const path = queryKey[1]
      const isIssuePath =
        path === '/api/v1/organizations/{orgCode}/issues' ||
        path === '/api/v1/organizations/{orgCode}/issues/{issueUid}' ||
        path === '/api/v1/organizations/{orgCode}/issues/stats'
      if (!isIssuePath) return false
      const params = queryKey[2]?.params as { path?: { orgCode?: string } } | undefined
      return params?.path?.orgCode === org
    }
  })
}

// Update asset property value mutation hook
export function useUpdateAssetPropertyValue(org: string, assetUid: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation('patch', '/api/v1/organizations/{orgCode}/assets/{assetUid}/properties/{propertyUid}', {
    onSuccess: () => {
      refreshAssetProperties(queryClient, org, assetUid)
    }
  })

  return {
    ...baseMutation,
    mutate: (propertyUid: string, data: any, options?: any) => {
      baseMutation.mutate({
        params: { path: { orgCode: org, assetUid, propertyUid }, query: { sync: true } },
        body: data
      }, options)
    },
    mutateAsync: (propertyUid: string, data: any, options?: any) => {
      return baseMutation.mutateAsync({
        params: { path: { orgCode: org, assetUid, propertyUid }, query: { sync: true } },
        body: data
      }, options)
    }
  }
}

// Delete asset property value mutation hook
export function useDeleteAssetPropertyValue(org: string, assetUid: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation('delete', '/api/v1/organizations/{orgCode}/assets/{assetUid}/properties/{propertyUid}', {
    onSuccess: () => {
      refreshAssetProperties(queryClient, org, assetUid)
    }
  })

  return {
    ...baseMutation,
    mutate: (propertyUid: string, options?: any) => {
      baseMutation.mutate({
        params: { path: { orgCode: org, assetUid, propertyUid }, query: { sync: true } }
      }, options)
    },
    mutateAsync: (propertyUid: string, options?: any) => {
      return baseMutation.mutateAsync({
        params: { path: { orgCode: org, assetUid, propertyUid }, query: { sync: true } }
      }, options)
    }
  }
}

// Confirm auto-propagated asset property value mutation hook
export function useConfirmAssetPropertyValue(org: string, assetUid: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation('post', '/api/v1/organizations/{orgCode}/assets/{assetUid}/properties/{propertyUid}/confirm', {
    onSuccess: () => {
      refreshAssetProperties(queryClient, org, assetUid)
    }
  })

  return {
    ...baseMutation,
    mutate: (propertyUid: string, options?: any) => {
      baseMutation.mutate({
        params: { path: { orgCode: org, assetUid, propertyUid } }
      }, options)
    }
  }
}

// Create asset mutation hook
export function useCreateAsset(org: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation('post', '/api/v1/organizations/{orgCode}/assets', {
    onSuccess: () => {
      refreshAsset(queryClient, org, undefined)
    }
  })

  return {
    ...baseMutation,
    mutate: (data: CreateAssetRequest, options?: any) => {
      baseMutation.mutate({
        params: { path: { orgCode: org } },
        body: data
      }, options)
    },
    mutateAsync: (data: CreateAssetRequest, options?: any) => {
      return baseMutation.mutateAsync({
        params: { path: { orgCode: org } },
        body: data
      }, options)
    }
  }
}

// Property definitions query hook
export function usePropertyDefinitions(
  org: string,
  options?: {
    entityType?: string
    dataTypes?: string[]
    search?: string
    categoryUid?: string
    enabled?: boolean
  }
) {
  const queryParams: Record<string, string> = {}
  if (options?.entityType) {
    queryParams.entity_type = options.entityType
  }
  if (options?.dataTypes && options.dataTypes.length > 0) {
    queryParams.type = options.dataTypes.join(',')
  }
  if (options?.search) {
    queryParams.q = options.search
  }
  if (options?.categoryUid) {
    queryParams.property_category_uid = options.categoryUid
  }

  // Every caller of this hook treats the result as "all the property
  // definitions", so it must not stop at one page. The server orders by
  // `code` and clamps `limit` to 500, so a single request silently dropped
  // every definition sorting after the 500th code once the seeded catalogue
  // outgrew that page. That is invisible rather than noisy: the affected
  // property simply never renders — `serial_number` (653 rows in the seed,
  // rank 522) vanished from the asset Properties tab and from the
  // suggestions "narrow by" list the moment the accessibility properties
  // pushed it past the boundary. `fetchAllPages` walks the pages instead,
  // so the result is a function of the catalogue and not of its size, and
  // `listQueryKey` preserves openapi-react-query's key shape so the cache
  // invalidation in `live-invalidation.ts` and the property-category
  // mutations still select this entry (spec 2026-09-03-03 §1).
  const queryPath = '/api/v1/organizations/{orgCode}/properties'
  const paginateParams = { path: { orgCode: org }, query: queryParams }
  const result = useQuery({
    queryKey: listQueryKey(queryPath, paginateParams),
    enabled: options?.enabled !== false && !!org,
    queryFn: () => fetchAllPages<PropertyDefinition>(queryPath, paginateParams),
  })

  return {
    ...result,
    data: result.data ?? (EMPTY_DATA_ARRAY as unknown as PropertyDefinition[])
  }
}

// Property definitions by UIDs query hook
export function usePropertyDefinitionsByUids(org: string, propertyUids?: string[]) {
  const uidsParam = propertyUids?.filter(uid => uid).join(',')

  const result = $api.useQuery('get', '/api/v1/organizations/{orgCode}/properties', {
    params: {
      path: { orgCode: org },
      query: uidsParam ? { uid: uidsParam } : undefined
    }
  }, {
    enabled: !!org && !!propertyUids && propertyUids.length > 0
  })

  return {
    ...result,
    data: extractData(result) as unknown as PropertyDefinition[]
  }
}

// Property definitions by codes query hook
export function usePropertyDefinitionsByCodes(org: string, codes?: string[]) {
  const codesParam = codes?.filter(c => c).join(',')

  const result = $api.useQuery('get', '/api/v1/organizations/{orgCode}/properties', {
    params: {
      path: { orgCode: org },
      query: codesParam ? { code: codesParam } : undefined
    }
  }, {
    enabled: !!org && !!codes && codes.length > 0
  })

  return {
    ...result,
    data: extractData(result) as unknown as PropertyDefinition[]
  }
}

// Property categories query hook
export function usePropertyCategories(org: string, options?: { search?: string; enabled?: boolean }) {
  const queryParams: Record<string, string> = {}
  if (options?.search) {
    queryParams.q = options.search
  }

  const result = $api.useQuery('get', '/api/v1/organizations/{orgCode}/property-categories', {
    params: {
      path: { orgCode: org },
      query: Object.keys(queryParams).length > 0 ? queryParams : undefined
    }
  }, {
    enabled: options?.enabled !== false && !!org
  })

  return {
    ...result,
    data: extractData(result) as unknown as PropertyCategory[]
  }
}

// Create property category mutation hook
export function useCreatePropertyCategory(org: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation('post', '/api/v1/organizations/{orgCode}/property-categories', {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.propertyCategories(org) })
      queryClient.invalidateQueries({
        predicate: (query) => {
          const [method, path] = query.queryKey
          return method === 'get' && path === '/api/v1/organizations/{orgCode}/property-categories'
        }
      })
    }
  })

  return {
    ...baseMutation,
    mutate: (data: { code: string; name: Record<string, string> }, options?: any) => {
      baseMutation.mutate({
        params: { path: { orgCode: org } },
        body: data
      }, options)
    },
    mutateAsync: (data: { code: string; name: Record<string, string> }) => {
      return baseMutation.mutateAsync({
        params: { path: { orgCode: org } },
        body: data
      })
    }
  }
}

// Update property category mutation hook
export function useUpdatePropertyCategory(org: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation('patch', '/api/v1/organizations/{orgCode}/property-categories/{propertyCategoryUid}', {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.propertyCategories(org) })
      queryClient.invalidateQueries({
        predicate: (query) => {
          const [method, path] = query.queryKey
          return method === 'get' && path === '/api/v1/organizations/{orgCode}/property-categories'
        }
      })
    }
  })

  return {
    ...baseMutation,
    mutate: (propertyCategoryUid: string, data: { code?: string; name?: Record<string, string> }, options?: any) => {
      baseMutation.mutate({
        params: { path: { orgCode: org, propertyCategoryUid } },
        body: data
      }, options)
    },
    mutateAsync: (propertyCategoryUid: string, data: { code?: string; name?: Record<string, string> }) => {
      return baseMutation.mutateAsync({
        params: { path: { orgCode: org, propertyCategoryUid } },
        body: data
      })
    }
  }
}

// Delete property category mutation hook
export function useDeletePropertyCategory(org: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation('delete', '/api/v1/organizations/{orgCode}/property-categories/{propertyCategoryUid}', {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.propertyCategories(org) })
      queryClient.invalidateQueries({
        predicate: (query) => {
          const [method, path] = query.queryKey
          return method === 'get' && (
            path === '/api/v1/organizations/{orgCode}/property-categories' ||
            path === '/api/v1/organizations/{orgCode}/properties'
          )
        }
      })
    }
  })

  return {
    ...baseMutation,
    mutate: (propertyCategoryUid: string, options?: any) => {
      baseMutation.mutate({
        params: { path: { orgCode: org, propertyCategoryUid } }
      }, options)
    },
    mutateAsync: (propertyCategoryUid: string) => {
      return baseMutation.mutateAsync({
        params: { path: { orgCode: org, propertyCategoryUid } }
      })
    }
  }
}

// Property definition query hook
export function usePropertyDefinition(org: string, propertyUid: string) {
  // Check for missing parameters
  const hasValidParams = !!org && !!propertyUid

  const result = $api.useQuery('get', '/api/v1/organizations/{orgCode}/properties/{propertyUid}', {
    params: {
      path: {
        orgCode: org,
        propertyUid: propertyUid
      }
    }
  }, {
    enabled: hasValidParams
  })

  return {
    ...result,
    data: extractItem(result) as PropertyDefinition,
    hasValidParams,
    missingParams: {
      org: !org,
      propertyUid: !propertyUid
    }
  }
}

// Create property definition mutation hook
export function useCreatePropertyDefinition(org: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation('post', '/api/v1/organizations/{orgCode}/properties', {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.propertyDefinitions(org) })
    }
  })

  return {
    ...baseMutation,
    mutate: (data: CreatePropertyDefinitionRequest, options?: any) => {
      baseMutation.mutate({
        params: { path: { orgCode: org } },
        body: data
      }, options)
    },
    mutateAsync: (data: CreatePropertyDefinitionRequest, options?: any) => {
      return baseMutation.mutateAsync({
        params: { path: { orgCode: org } },
        body: data
      }, options)
    }
  }
}

// Update property definition mutation hook
export function useUpdatePropertyDefinition(org: string, propertyUid: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation('patch', '/api/v1/organizations/{orgCode}/properties/{propertyUid}', {
    onSuccess: async () => {
      // Invalidate all property-related queries
      await queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey as any[]
          // Match queries that include the properties endpoint
          return key.some(k =>
            typeof k === 'string' &&
            (k.includes('/properties') || k.includes('propertyDefinition'))
          )
        }
      })
    }
  })

  return {
    ...baseMutation,
    mutate: (data: UpdatePropertyDefinitionRequest, options?: any) => {
      baseMutation.mutate({
        params: { path: { orgCode: org, propertyUid } },
        body: data
      }, options)
    },
    mutateAsync: (data: UpdatePropertyDefinitionRequest, options?: any) => {
      return baseMutation.mutateAsync({
        params: { path: { orgCode: org, propertyUid } },
        body: data
      }, options)
    }
  }
}

// Delete property definition mutation hook
export function useDeletePropertyDefinition(org: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation('delete', '/api/v1/organizations/{orgCode}/properties/{propertyUid}', {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.propertyDefinitions(org) })
    }
  })

  return {
    ...baseMutation,
    mutate: (propertyUid: string, options?: any) => {
      baseMutation.mutate({
        params: { path: { orgCode: org, propertyUid } }
      }, options)
    },
    mutateAsync: (propertyUid: string, options?: any) => {
      return baseMutation.mutateAsync({
        params: { path: { orgCode: org, propertyUid } }
      }, options)
    }
  }
}

// Property enum values query hook
export function usePropertyEnumValues(org: string, propertyUid: string, options?: { enabled?: boolean }) {
  const result = $api.useQuery('get', '/api/v1/organizations/{orgCode}/properties/{propertyUid}/enum-values', {
    params: {
      path: {
        orgCode: org,
        propertyUid: propertyUid
      }
    }
  }, {
    enabled: options?.enabled !== false && !!org && !!propertyUid
  })

  return {
    ...result,
    data: extractData(result) as PropertyEnumValue[]
  }
}

// Create property enum value mutation hook
export function useCreatePropertyEnumValue(org: string, propertyUid: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation('post', '/api/v1/organizations/{orgCode}/properties/{propertyUid}/enum-values', {
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey as any[]
          return queryKey.length >= 2 && queryKey[0] === 'get' &&
            queryKey[1] === '/api/v1/organizations/{orgCode}/properties/{propertyUid}/enum-values'
        }
      })
    }
  })

  return {
    ...baseMutation,
    mutate: (data: CreatePropertyEnumValueRequest, options?: any) => {
      baseMutation.mutate({
        params: { path: { orgCode: org, propertyUid } },
        body: data
      }, options)
    },
    mutateAsync: (data: CreatePropertyEnumValueRequest, options?: any) => {
      return baseMutation.mutateAsync({
        params: { path: { orgCode: org, propertyUid } },
        body: data
      }, options)
    }
  }
}

// Update property enum value mutation hook
export function useUpdatePropertyEnumValue(org: string, propertyUid: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation('patch', '/api/v1/organizations/{orgCode}/properties/{propertyUid}/enum-values/{enumValueUid}', {
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey as any[]
          return queryKey.length >= 2 && queryKey[0] === 'get' &&
            queryKey[1] === '/api/v1/organizations/{orgCode}/properties/{propertyUid}/enum-values'
        }
      })
    }
  })

  return {
    ...baseMutation,
    mutate: (enumValueUid: string, data: UpdatePropertyEnumValueRequest, options?: any) => {
      baseMutation.mutate({
        params: { path: { orgCode: org, propertyUid, enumValueUid } },
        body: data
      }, options)
    },
    mutateAsync: (enumValueUid: string, data: UpdatePropertyEnumValueRequest, options?: any) => {
      return baseMutation.mutateAsync({
        params: { path: { orgCode: org, propertyUid, enumValueUid } },
        body: data
      }, options)
    }
  }
}

// Delete property enum value mutation hook
export function useDeletePropertyEnumValue(org: string, propertyUid: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation('delete', '/api/v1/organizations/{orgCode}/properties/{propertyUid}/enum-values/{enumValueUid}', {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.propertyEnumValues(org, propertyUid) })
    }
  })

  return {
    ...baseMutation,
    mutate: (enumValueUid: string, options?: any) => {
      baseMutation.mutate({
        params: { path: { orgCode: org, propertyUid, enumValueUid } }
      }, options)
    },
    mutateAsync: (enumValueUid: string, options?: any) => {
      return baseMutation.mutateAsync({
        params: { path: { orgCode: org, propertyUid, enumValueUid } }
      }, options)
    }
  }
}

// Organization query hook
export function useOrganization(org: string) {
  const result = $api.useQuery('get', '/api/v1/organizations/{orgCode}', {
    params: {
      path: { orgCode: org }
    }
  })

  return {
    ...result,
    data: extractItem(result) as OrganizationData
  }
}

// Asset type properties query hook
export function useAssetTypeProperties(org: string, assetTypeUid: string, inherited: boolean = false) {
  const result = $api.useQuery('get', '/api/v1/organizations/{orgCode}/asset-types/{assetTypeUid}/properties', {
    params: {
      path: {
        orgCode: org,
        assetTypeUid: assetTypeUid
      },
      query: inherited ? { inherited: true } : undefined
    }
  })

  return {
    ...result,
    data: extractData(result) as unknown as PropertyDefinition[]
  }
}

// Asset type inherited properties query hook  
export function useAssetTypeInheritedProperties(org: string, assetTypeUid: string, _options?: { enabled?: boolean }) {
  return useAssetTypeProperties(org, assetTypeUid, true)
}

// Assets by asset type query hook
export function useAssetsByAssetType(org: string, assetTypeUid: string, options?: { enabled?: boolean }) {
  const result = $api.useQuery('get', '/api/v1/organizations/{orgCode}/assets', {
    params: {
      path: { orgCode: org },
      query: { asset_type_uid: assetTypeUid }
    }
  }, {
    enabled: options?.enabled !== false && !!org && !!assetTypeUid
  })

  return {
    ...result,
    data: extractData(result) as Asset[]
  }
}

/**
 * How many assets use an asset type: live and archived. Both block a delete
 * (spec 2026-10-11-28). Filters on the type's uid, so a `unit` count does not
 * widen to its lot subtypes the way `asset_type_code` does.
 */
export function useAssetTypeUsage(org: string, assetTypeUid: string) {
  const enabled = !!org && !!assetTypeUid
  const live = $api.useQuery('get', '/api/v1/organizations/{orgCode}/assets', {
    params: { path: { orgCode: org }, query: { asset_type_uid: assetTypeUid, limit: 1 } as any }
  }, { enabled })
  const archived = $api.useQuery('get', '/api/v1/organizations/{orgCode}/assets', {
    params: { path: { orgCode: org }, query: { asset_type_uid: assetTypeUid, archived: true, limit: 1 } as any }
  }, { enabled })
  const liveCount = (live.data as { total?: number } | undefined)?.total ?? 0
  const archivedCount = (archived.data as { total?: number } | undefined)?.total ?? 0
  return {
    isLoading: live.isLoading || archived.isLoading,
    liveCount,
    archivedCount,
    total: liveCount + archivedCount
  }
}

function invalidateAssetTypes(queryClient: ReturnType<typeof useQueryClient>, org: string) {
  queryClient.invalidateQueries({ queryKey: queryKeys.assetTypes(org) })
  queryClient.invalidateQueries({
    predicate: (query) => {
      const [method, path] = query.queryKey as unknown[]
      return method === 'get' && typeof path === 'string' && path.startsWith('/api/v1/organizations/{orgCode}/asset-types')
    }
  })
}

/** Delete / archive / restore an asset type (spec 2026-10-11-28). */
export function useDeleteAssetType(org: string) {
  const queryClient = useQueryClient()
  return $api.useMutation('delete', '/api/v1/organizations/{orgCode}/asset-types/{assetTypeUid}', {
    onSuccess: () => invalidateAssetTypes(queryClient, org)
  })
}

export function useArchiveAssetType(org: string) {
  const queryClient = useQueryClient()
  return $api.useMutation('post', '/api/v1/organizations/{orgCode}/asset-types/{assetTypeUid}/archive', {
    onSuccess: () => invalidateAssetTypes(queryClient, org)
  })
}

export function useRestoreAssetType(org: string) {
  const queryClient = useQueryClient()
  return $api.useMutation('post', '/api/v1/organizations/{orgCode}/asset-types/{assetTypeUid}/restore', {
    onSuccess: () => invalidateAssetTypes(queryClient, org)
  })
}

// Property asset types query hook
export function usePropertyAssetTypes(org: string, propertyUid: string) {
  const result = $api.useQuery('get', '/api/v1/organizations/{orgCode}/properties/{propertyUid}/asset-types', {
    params: {
      path: {
        orgCode: org,
        propertyUid: propertyUid
      }
    }
  })

  return {
    ...result,
    data: extractData(result) as unknown as AssetType[]
  }
}

// Property usage query hook
export function usePropertyUsage(org: string, propertyUid: string, sampleLimit?: number) {
  const queryParams: Record<string, string> = {}
  if (sampleLimit) {
    queryParams.sample_limit = sampleLimit.toString()
  }

  return $api.useQuery('get', '/api/v1/organizations/{orgCode}/properties/{propertyUid}/usage', {
    params: {
      path: {
        orgCode: org,
        propertyUid: propertyUid
      },
      query: Object.keys(queryParams).length > 0 ? queryParams : undefined
    }
  })
}

// Add asset type property mutation hook
export function useAddAssetTypeProperty(org: string, assetTypeUid: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation('put', '/api/v1/organizations/{orgCode}/asset-types/{assetTypeUid}/properties/{propertyUid}', {
    onSuccess: () => {
      // Invalidate all queries for asset type properties (including inherited)
      // The auto-generated query key from openapi-react-query includes the path and query params
      queryClient.invalidateQueries({
        predicate: (query) => {
          const [method, path] = query.queryKey
          return method === 'get' && path === '/api/v1/organizations/{orgCode}/asset-types/{assetTypeUid}/properties'
        }
      })
    }
  })

  return {
    ...baseMutation,
    mutate: (propertyUid: string, options?: any) => {
      baseMutation.mutate({
        params: { path: { orgCode: org, assetTypeUid, propertyUid } }
      }, options)
    },
    mutateAsync: (propertyUid: string, options?: any) => {
      return baseMutation.mutateAsync({
        params: { path: { orgCode: org, assetTypeUid, propertyUid } }
      }, options)
    }
  }
}

// Remove asset type property mutation hook
export function useRemoveAssetTypeProperty(org: string, assetTypeUid: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation('delete', '/api/v1/organizations/{orgCode}/asset-types/{assetTypeUid}/properties/{propertyUid}', {
    onSuccess: () => {
      // Invalidate all queries for asset type properties (including inherited)
      // The auto-generated query key from openapi-react-query includes the path and query params
      queryClient.invalidateQueries({
        predicate: (query) => {
          const [method, path] = query.queryKey
          return method === 'get' && path === '/api/v1/organizations/{orgCode}/asset-types/{assetTypeUid}/properties'
        }
      })
    }
  })

  return {
    ...baseMutation,
    mutate: (propertyUid: string, options?: any) => {
      baseMutation.mutate({
        params: { path: { orgCode: org, assetTypeUid, propertyUid } }
      }, options)
    },
    mutateAsync: (propertyUid: string, options?: any) => {
      return baseMutation.mutateAsync({
        params: { path: { orgCode: org, assetTypeUid, propertyUid } }
      }, options)
    }
  }
}

// Update asset type property binding (requirement + completeness_weight)
export function useUpdateAssetTypeProperty(org: string, assetTypeUid: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation('patch', '/api/v1/organizations/{orgCode}/asset-types/{assetTypeUid}/properties/{propertyUid}', {
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) => {
          const [method, path] = query.queryKey
          return method === 'get' && path === '/api/v1/organizations/{orgCode}/asset-types/{assetTypeUid}/properties'
        }
      })
    }
  })

  return {
    ...baseMutation,
    mutateAsync: (propertyUid: string, body: { requirement?: 'optional' | 'recommended' | 'mandatory'; completeness_weight?: number }, options?: any) => {
      return baseMutation.mutateAsync({
        params: { path: { orgCode: org, assetTypeUid, propertyUid } },
        body
      }, options)
    }
  }
}

// Asset with parent chain using the /assets endpoint with uid and asset_chain=true
export function useAssetWithParentChain(org: string, assetUid: string) {
  const result = $api.useQuery('get', '/api/v1/organizations/{orgCode}/assets', {
    params: {
      path: { orgCode: org },
      query: {
        uid: [assetUid],
        asset_chain: true
      }
    }
  }, {
    enabled: !!org && !!assetUid
  })

  return {
    ...result,
    data: extractData(result) as Asset[]
  }
}

// Asset parent chain query hook - Currently not supported by the API
// export function useAssetParentChain(org: string, assetUid?: string) {
//   // This endpoint doesn't exist in the API schema
// }

// Document Type Properties hooks

// Document type properties query hook
export function useDocumentTypeProperties(org: string, documentTypeUid: string) {
  const result = $api.useQuery('get', '/api/v1/organizations/{orgCode}/document-types/{documentTypeUid}/properties', {
    params: {
      path: {
        orgCode: org,
        documentTypeUid: documentTypeUid
      }
    }
  }, {
    enabled: !!org && !!documentTypeUid
  })

  return {
    ...result,
    data: extractData(result) as unknown as PropertyDefinition[]
  }
}

// Add document type property mutation hook
export function useAddDocumentTypeProperty(org: string, documentTypeUid: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation('put', '/api/v1/organizations/{orgCode}/document-types/{documentTypeUid}/properties/{propertyUid}', {
    onSuccess: () => {
      // Invalidate all queries for document type properties
      queryClient.invalidateQueries({
        predicate: (query) => {
          const [method, path] = query.queryKey
          return method === 'get' && path === '/api/v1/organizations/{orgCode}/document-types/{documentTypeUid}/properties'
        }
      })
    }
  })

  return {
    ...baseMutation,
    mutate: (propertyUid: string, options?: any) => {
      baseMutation.mutate({
        params: { path: { orgCode: org, documentTypeUid, propertyUid } }
      }, options)
    },
    mutateAsync: (propertyUid: string, options?: any) => {
      return baseMutation.mutateAsync({
        params: { path: { orgCode: org, documentTypeUid, propertyUid } }
      }, options)
    }
  }
}

// Remove document type property mutation hook
export function useRemoveDocumentTypeProperty(org: string, documentTypeUid: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation('delete', '/api/v1/organizations/{orgCode}/document-types/{documentTypeUid}/properties/{propertyUid}', {
    onSuccess: () => {
      // Invalidate all queries for document type properties
      queryClient.invalidateQueries({
        predicate: (query) => {
          const [method, path] = query.queryKey
          return method === 'get' && path === '/api/v1/organizations/{orgCode}/document-types/{documentTypeUid}/properties'
        }
      })
    }
  })

  return {
    ...baseMutation,
    mutate: (propertyUid: string, options?: any) => {
      baseMutation.mutate({
        params: { path: { orgCode: org, documentTypeUid, propertyUid } }
      }, options)
    },
    mutateAsync: (propertyUid: string, options?: any) => {
      return baseMutation.mutateAsync({
        params: { path: { orgCode: org, documentTypeUid, propertyUid } }
      }, options)
    }
  }
}

// Document Property Value hooks

export type DocumentPropertyValue = {
  property_uid?: string
  value?: unknown
}

// Document properties query hook
export function useDocumentProperties(org: string, documentUid: string) {
  const hasValidParams = !!org && !!documentUid
  const result = $api.useQuery('get', '/api/v1/organizations/{orgCode}/documents/{documentUid}/properties', {
    params: {
      path: {
        orgCode: org,
        documentUid: documentUid
      }
    }
  }, {
    enabled: hasValidParams
  })

  return {
    ...result,
    data: extractData(result) as DocumentPropertyValue[]
  }
}

// Set document property value mutation hook
export function useSetDocumentPropertyValue(org: string, documentUid: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation('put', '/api/v1/organizations/{orgCode}/documents/{documentUid}/properties/{propertyUid}', {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.documentProperties(org, documentUid) })
      queryClient.invalidateQueries({ queryKey: queryKeys.document(org, documentUid) })
    }
  })

  return {
    ...baseMutation,
    mutate: (data: any, options?: any) => {
      const { properties } = data
      if (properties && properties.length > 0) {
        const property = properties[0]
        baseMutation.mutate({
          params: { path: { orgCode: org, documentUid, propertyUid: property.property_uid } },
          body: { value: property.value }
        }, options)
      }
    },
    mutateAsync: async (data: any, options?: any) => {
      const { properties } = data
      if (properties && properties.length > 0) {
        const property = properties[0]
        return baseMutation.mutateAsync({
          params: { path: { orgCode: org, documentUid, propertyUid: property.property_uid } },
          body: { value: property.value }
        }, options)
      }
    }
  }
}

export function refreshDocumentProperties(queryClient: QueryClient, org: string, documentUid: string) {
  queryClient.invalidateQueries({ queryKey: queryKeys.documentProperties(org, documentUid) })
  queryClient.invalidateQueries({
    predicate: query => {
      // Check if this is a document properties query for the same document
      const queryKey = query.queryKey as any[]
      if (queryKey.length >= 2 && queryKey[0] === 'get') {
        const path = queryKey[1]
        // Match the exact endpoint path
        if (path === '/api/v1/organizations/{orgCode}/documents/{documentUid}/properties') {
          // Check if the params match our org and documentUid
          const params = queryKey[2]?.params as { path: { orgCode: string, documentUid: string } }
          if (params?.path?.orgCode === org && params?.path?.documentUid === documentUid) {
            return true
          }
        }
      }
      return false
    }
  })
}

// Update document property value mutation hook
export function useUpdateDocumentPropertyValue(org: string, documentUid: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation('patch', '/api/v1/organizations/{orgCode}/documents/{documentUid}/properties/{propertyUid}', {
    onSuccess: () => {
      refreshDocumentProperties(queryClient, org, documentUid)
    }
  })

  return {
    ...baseMutation,
    mutate: (propertyUid: string, data: any, options?: any) => {
      baseMutation.mutate({
        params: { path: { orgCode: org, documentUid, propertyUid } },
        body: data
      }, options)
    },
    mutateAsync: (propertyUid: string, data: any, options?: any) => {
      return baseMutation.mutateAsync({
        params: { path: { orgCode: org, documentUid, propertyUid } },
        body: data
      }, options)
    }
  }
}

// Delete document property value mutation hook
export function useDeleteDocumentPropertyValue(org: string, documentUid: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation('delete', '/api/v1/organizations/{orgCode}/documents/{documentUid}/properties/{propertyUid}', {
    onSuccess: () => {
      refreshDocumentProperties(queryClient, org, documentUid)
    }
  })

  return {
    ...baseMutation,
    mutate: (propertyUid: string, options?: any) => {
      baseMutation.mutate({
        params: { path: { orgCode: org, documentUid, propertyUid } }
      }, options)
    },
    mutateAsync: (propertyUid: string, options?: any) => {
      return baseMutation.mutateAsync({
        params: { path: { orgCode: org, documentUid, propertyUid } }
      }, options)
    }
  }
}

// Contract Types

export type ContractType = components['schemas']['ContractType'] & {
  /** Legacy shape: the API sends `names`. Read both through `contractTypeLabel`. */
  name?: Record<string, string> | string
  description?: Record<string, string> | string
}

export type ContractStakeholderType = components['schemas']['ContractStakeholderType'] & {
  name: Record<string, string> | string
  description?: Record<string, string> | string
}

export type Contract = components['schemas']['Contract'] & {
  title: Record<string, string> | string
  description?: Record<string, string> | string
}

export type ContractStakeholder = components['schemas']['ContractStakeholder']

export type ContractAsset = components['schemas']['ContractAsset']

// Typed clause values (contract_data) and the contract-type property junction.
export type ContractPropertyValue = components['schemas']['ContractPropertyValue']

export type ContractTypeProperty = components['schemas']['ContractTypeProperty']

export type CreateContractTypeRequest = components['schemas']['CreateContractTypeRequest']

export type UpdateContractTypeRequest = components['schemas']['UpdateContractTypeRequest']

export type CreateContractStakeholderTypeRequest = components['schemas']['CreateContractStakeholderTypeRequest']

export type UpdateContractStakeholderTypeRequest = components['schemas']['UpdateContractStakeholderTypeRequest']

export type CreateContractRequest = components['schemas']['CreateContractRequest']

export type UpdateContractRequest = components['schemas']['UpdateContractRequest']

export type CreateContractStakeholderRequest = components['schemas']['CreateContractStakeholderRequest']

export type UpdateContractStakeholderRequest = components['schemas']['UpdateContractStakeholderRequest']

export type CreateContractAssetRequest = components['schemas']['CreateContractAssetRequest']

// Expense (financial actual). The list endpoint eager-loads the `asset` and
// `supplier` relations on each item — those nested objects are not part of the
// generated OpenAPI schema, so we widen the type here.
export type Expense = components['schemas']['Expense'] & {
  asset?: { uid?: string; name?: string } | null
  supplier?: { uid?: string; name?: string; email?: string } | null
}

// Expenses list query hook — mirrors the backend filters (document_uid,
// asset_uid + subtree, intervention_uid, contract_uid, date range, pagination).
export function useExpenses(org: string, options?: {
  assetUid?: string
  subtree?: boolean
  interventionUid?: string
  contractUid?: string
  documentUid?: string
  supplierUserUid?: string
  dateFrom?: string
  dateTo?: string
  page?: number
  limit?: number
  enabled?: boolean
  /** Adds each line's recoverable proposal (spec 2026-10-25-09). */
  includeRecoverableProposal?: boolean
}) {
  const queryParams: Record<string, string | number | boolean> = {}
  if (options?.includeRecoverableProposal) queryParams.include = 'recoverable_proposal'
  if (options?.assetUid) queryParams.asset_uid = options.assetUid
  if (options?.subtree) queryParams.subtree = options.subtree
  if (options?.interventionUid) queryParams.intervention_uid = options.interventionUid
  if (options?.contractUid) queryParams.contract_uid = options.contractUid
  if (options?.documentUid) queryParams.document_uid = options.documentUid
  if (options?.supplierUserUid) queryParams.supplier_user_uid = options.supplierUserUid
  if (options?.dateFrom) queryParams.date_from = options.dateFrom
  if (options?.dateTo) queryParams.date_to = options.dateTo
  if (options?.page) queryParams.page = options.page
  if (options?.limit) queryParams.limit = options.limit

  const result = $api.useQuery('get', '/api/v1/organizations/{orgCode}/expenses', {
    params: {
      path: { orgCode: org },
      query: Object.keys(queryParams).length > 0 ? queryParams : undefined,
    },
  }, {
    enabled: options?.enabled !== false && !!org,
  })

  return {
    ...result,
    data: extractData(result) as unknown as Expense[],
  }
}

// Single expense query hook
export function useExpense(org: string, expenseUid: string) {
  const result = $api.useQuery('get', '/api/v1/organizations/{orgCode}/expenses/{expenseUid}', {
    params: {
      path: { orgCode: org, expenseUid },
    },
  }, {
    enabled: !!org && !!expenseUid,
  })

  return {
    ...result,
    data: extractItem(result) as Expense | undefined,
  }
}

export type UpdateExpenseRequest = components['schemas']['UpdateExpenseRequest']

// Invalidates the $api-cached expense queries (single + list, any params) AND
// the bespoke `['expenses', org, interventionUid]` cache used by
// `useInterventions.ts`'s drawer/tab hooks — both caches back the same
// backend rows, so a PATCH/DELETE here must bust both.
function invalidateExpenseCaches(queryClient: QueryClient, org: string) {
  queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey as any[]
      if (key[0] === 'get' && typeof key[1] === 'string' && key[1].includes('/organizations/{orgCode}/expenses')) {
        return true
      }
      return key[0] === 'expenses' && key[1] === org
    },
  })
}

// Update expense mutation hook
export function useUpdateExpense(org: string, expenseUid: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation('patch', '/api/v1/organizations/{orgCode}/expenses/{expenseUid}', {
    onSuccess: () => {
      invalidateExpenseCaches(queryClient, org)
    }
  })

  return {
    ...baseMutation,
    mutate: (data: UpdateExpenseRequest, options?: any) => {
      baseMutation.mutate({
        params: { path: { orgCode: org, expenseUid } },
        body: data
      }, options)
    },
    mutateAsync: (data: UpdateExpenseRequest, options?: any) => {
      return baseMutation.mutateAsync({
        params: { path: { orgCode: org, expenseUid } },
        body: data
      }, options)
    }
  }
}

// Delete expense mutation hook
export function useDeleteExpense(org: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation('delete', '/api/v1/organizations/{orgCode}/expenses/{expenseUid}', {
    onSuccess: () => {
      invalidateExpenseCaches(queryClient, org)
    }
  })

  return {
    ...baseMutation,
    mutate: (expenseUid: string, options?: any) => {
      baseMutation.mutate({
        params: { path: { orgCode: org, expenseUid } }
      }, options)
    },
    mutateAsync: (expenseUid: string, options?: any) => {
      return baseMutation.mutateAsync({
        params: { path: { orgCode: org, expenseUid } }
      }, options)
    }
  }
}

// Contract types query hook
export function useContractTypes(org: string, options?: { enabled?: boolean }) {
  const result = $api.useQuery('get', '/api/v1/organizations/{orgCode}/contract-types', {
    params: {
      path: { orgCode: org }
    }
  }, {
    enabled: options?.enabled !== false && !!org
  })

  return {
    ...result,
    data: extractData(result) as unknown as ContractType[]
  }
}

// Contract type query hook
export function useContractType(org: string, contractTypeUid: string) {
  const result = $api.useQuery('get', '/api/v1/organizations/{orgCode}/contract-types/{contractTypeUid}', {
    params: {
      path: {
        orgCode: org,
        contractTypeUid: contractTypeUid
      }
    }
  }, {
    enabled: !!org && !!contractTypeUid
  })

  return {
    ...result,
    data: extractItem(result) as ContractType
  }
}

// Create contract type mutation hook
export function useCreateContractType(org: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation('post', '/api/v1/organizations/{orgCode}/contract-types', {
    onSuccess: () => {
      // Invalidate using queryKeys
      queryClient.invalidateQueries({ queryKey: queryKeys.contractTypes(org) })
      // Also invalidate any list queries
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey as any[]
          return key && key.length >= 2 && key[0] === 'get' &&
            key[1] === '/api/v1/organizations/{orgCode}/contract-types'
        }
      })
    }
  })

  return {
    ...baseMutation,
    mutate: (data: CreateContractTypeRequest, options?: any) => {
      baseMutation.mutate({
        params: { path: { orgCode: org } },
        body: data
      }, options)
    },
    mutateAsync: (data: CreateContractTypeRequest, options?: any) => {
      return baseMutation.mutateAsync({
        params: { path: { orgCode: org } },
        body: data
      }, options)
    }
  }
}

// Update contract type mutation hook
export function useUpdateContractType(org: string, contractTypeUid: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation('patch', '/api/v1/organizations/{orgCode}/contract-types/{contractTypeUid}', {
    onSuccess: () => {
      // Invalidate the specific contract type
      queryClient.invalidateQueries({ queryKey: queryKeys.contractTypes(org, contractTypeUid) })
      // Invalidate the list of contract types
      queryClient.invalidateQueries({ queryKey: queryKeys.contractTypes(org) })
      // Also invalidate any list queries using predicate
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey as any[]
          return key && key.length >= 2 && key[0] === 'get' &&
            key[1] === '/api/v1/organizations/{orgCode}/contract-types'
        }
      })
    }
  })

  return {
    ...baseMutation,
    mutate: (data: UpdateContractTypeRequest, options?: any) => {
      baseMutation.mutate({
        params: { path: { orgCode: org, contractTypeUid } },
        body: data
      }, options)
    },
    mutateAsync: (data: UpdateContractTypeRequest, options?: any) => {
      return baseMutation.mutateAsync({
        params: { path: { orgCode: org, contractTypeUid } },
        body: data
      }, options)
    }
  }
}

// Delete contract type mutation hook
export function useDeleteContractType(org: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation('delete', '/api/v1/organizations/{orgCode}/contract-types/{contractTypeUid}', {
    onSuccess: () => {
      // Invalidate the list of contract types
      queryClient.invalidateQueries({ queryKey: queryKeys.contractTypes(org) })
      // Also invalidate any list queries using predicate
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey as any[]
          return key && key.length >= 2 && key[0] === 'get' &&
            key[1] === '/api/v1/organizations/{orgCode}/contract-types'
        }
      })
    }
  })

  return {
    ...baseMutation,
    mutate: (contractTypeUid: string, options?: any) => {
      baseMutation.mutate({
        params: { path: { orgCode: org, contractTypeUid } }
      }, options)
    },
    mutateAsync: (contractTypeUid: string, options?: any) => {
      return baseMutation.mutateAsync({
        params: { path: { orgCode: org, contractTypeUid } }
      }, options)
    }
  }
}

// Contract stakeholder types query hook
export function useContractStakeholderTypes(org: string, options?: { enabled?: boolean; limit?: number }) {
  const queryParams: Record<string, string | number> = {}
  if (options?.limit) {
    queryParams.limit = options.limit
  }

  const result = $api.useQuery('get', '/api/v1/organizations/{orgCode}/contract-stakeholder-types', {
    params: {
      path: { orgCode: org },
      query: Object.keys(queryParams).length > 0 ? queryParams : undefined
    }
  }, {
    enabled: options?.enabled !== false && !!org
  })

  return {
    ...result,
    data: extractData(result) as unknown as ContractStakeholderType[]
  }
}

// Contract stakeholder type query hook
export function useContractStakeholderType(org: string, stakeholderTypeUid: string) {
  const result = $api.useQuery('get', '/api/v1/organizations/{orgCode}/contract-stakeholder-types/{stakeholderTypeUid}', {
    params: {
      path: {
        orgCode: org,
        stakeholderTypeUid: stakeholderTypeUid
      }
    }
  }, {
    enabled: !!org && !!stakeholderTypeUid
  })

  return {
    ...result,
    data: extractItem(result) as ContractStakeholderType
  }
}

// Create contract stakeholder type mutation hook
export function useCreateContractStakeholderType(org: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation('post', '/api/v1/organizations/{orgCode}/contract-stakeholder-types', {
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) => {
          const [method, path] = query.queryKey
          return method === 'get' && path === '/api/v1/organizations/{orgCode}/contract-stakeholder-types'
        }
      })
    }
  })

  return {
    ...baseMutation,
    mutate: (data: CreateContractStakeholderTypeRequest, options?: any) => {
      baseMutation.mutate({
        params: { path: { orgCode: org } },
        body: data
      }, options)
    },
    mutateAsync: (data: CreateContractStakeholderTypeRequest, options?: any) => {
      return baseMutation.mutateAsync({
        params: { path: { orgCode: org } },
        body: data
      }, options)
    }
  }
}

// Update contract stakeholder type mutation hook
export function useUpdateContractStakeholderType(org: string, stakeholderTypeUid: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation('patch', '/api/v1/organizations/{orgCode}/contract-stakeholder-types/{stakeholderTypeUid}', {
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) => {
          const [method, path] = query.queryKey
          return method === 'get' && path === '/api/v1/organizations/{orgCode}/contract-stakeholder-types'
        }
      })
    }
  })

  return {
    ...baseMutation,
    mutate: (data: UpdateContractStakeholderTypeRequest, options?: any) => {
      baseMutation.mutate({
        params: { path: { orgCode: org, stakeholderTypeUid } },
        body: data
      }, options)
    },
    mutateAsync: (data: UpdateContractStakeholderTypeRequest, options?: any) => {
      return baseMutation.mutateAsync({
        params: { path: { orgCode: org, stakeholderTypeUid } },
        body: data
      }, options)
    }
  }
}

// Delete contract stakeholder type mutation hook
export function useDeleteContractStakeholderType(org: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation('delete', '/api/v1/organizations/{orgCode}/contract-stakeholder-types/{stakeholderTypeUid}', {
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) => {
          const [method, path] = query.queryKey
          return method === 'get' && path === '/api/v1/organizations/{orgCode}/contract-stakeholder-types'
        }
      })
    }
  })

  return {
    ...baseMutation,
    mutate: (stakeholderTypeUid: string, options?: any) => {
      baseMutation.mutate({
        params: { path: { orgCode: org, stakeholderTypeUid } }
      }, options)
    },
    mutateAsync: (stakeholderTypeUid: string, options?: any) => {
      return baseMutation.mutateAsync({
        params: { path: { orgCode: org, stakeholderTypeUid } }
      }, options)
    }
  }
}

// Contracts query hook
export function useContracts(org: string, options?: {
  contractTypeUid?: string
  lightweight?: boolean
  status?: string
  assetUid?: string
  assetIncludeChildren?: boolean
  userUid?: string
  search?: string
  page?: number
  limit?: number
  include?: string[]
  sortBy?: 'code' | 'name' | 'start_date' | 'end_date' | 'created_at' | 'updated_at'
  sortDir?: 'asc' | 'desc'
  // When true, keep only chain-head contracts (drop those referenced as another
  // live contract's previous_contract_id). The Contracts table sets this so a
  // superseded contract whose code is reused by its successor is not duplicated.
  headsOnly?: boolean
  // Keep only open contracts (closed_at null) whose termination_date is on/before
  // this YYYY-MM-DD — the global "contracts to denounce soon" query.
  terminationBefore?: string
  // With terminationBefore, the notice window the sidebar badge opens (spec
  // 2026-10-27-23): contracts in force whose termination_date is on/after this.
  terminationFrom?: string
  // Filter on the `framework` flag (spec 2026-08-01-02): true = framework
  // contracts only (Cadres), false = everything that is not one (Applications
  // and standalone contracts), undefined = no filter.
  framework?: boolean
  // Keep only the application contracts of one framework — the framework detail
  // page's applications list.
  frameworkContractUid?: string
  // Tri-state PII filter (spec 2026-08-13-02 §6), expanded server-side onto
  // the contract's pii:* tokens. Deliberately independent of `individual`.
  pii?: 'true' | 'false' | 'unknown'
  // Keep only contracts with (true) / without (false) a natural-person or
  // party-group stakeholder. undefined = no filter.
  individual?: boolean
  // Keep only the leases running today (lease family, open or in progress,
  // started and not yet ended or terminated). Decided server-side.
  runningLease?: boolean
  enabled?: boolean
}) {
  const queryParams: Record<string, string | number | boolean> = {}
  if (options?.contractTypeUid) queryParams.contract_type_uid = options.contractTypeUid
  if (options?.lightweight !== undefined) queryParams.lightweight = options.lightweight
  if (options?.status) queryParams.status = options.status
  if (options?.assetUid) queryParams.asset_uid = options.assetUid
  if (options?.assetIncludeChildren) queryParams.asset_include_children = 'true'
  if (options?.runningLease) queryParams.running_lease = 'true'
  if (options?.userUid) queryParams.user_uid = options.userUid
  if (options?.search) queryParams.q = options.search
  if (options?.page) queryParams.page = options.page
  if (options?.limit) queryParams.limit = options.limit
  if (options?.include) queryParams.include = options.include.join(',')
  if (options?.sortBy) queryParams.sort_by = options.sortBy
  if (options?.sortDir) queryParams.sort_dir = options.sortDir
  if (options?.headsOnly) queryParams.heads_only = 'true'
  if (options?.terminationBefore) queryParams.termination_before = options.terminationBefore
  if (options?.terminationFrom) queryParams.termination_from = options.terminationFrom
  if (options?.framework !== undefined) queryParams.framework = options.framework
  if (options?.frameworkContractUid) queryParams.framework_contract_uid = options.frameworkContractUid
  if (options?.pii) queryParams.pii = options.pii
  if (options?.individual !== undefined) queryParams.individual = options.individual
  const result = $api.useQuery('get', '/api/v1/organizations/{orgCode}/contracts', {
    params: {
      path: { orgCode: org },
      query: Object.keys(queryParams).length > 0 ? queryParams : undefined
    }
  }, {
    enabled: options?.enabled !== false
  })

  return {
    ...result,
    data: extractData(result) as unknown as Contract[],
    // Pagination envelope total, so pagers know the page count.
    total: (result.data as { total?: number } | undefined)?.total ?? 0
  }
}

// Page size for useAllContracts. ListContracts clamps any `limit` above 100
// back to 50, so 100 is the largest page the walk can ask for.
export const CONTRACTS_PAGE_SIZE = 100

// Every contract matching the filters, walking the pages (the timeline draws
// one lane per contract and must not stop at the first page). The key keeps
// the generated `['get', path, { params }]` shape so live invalidation still
// selects it.
export function useAllContracts(org: string, options?: {
  contractTypeUid?: string
  lightweight?: boolean
  status?: string
  assetUid?: string
  assetIncludeChildren?: boolean
  search?: string
  include?: string[]
  // Keep only chain-head contracts, as useContracts' headsOnly.
  headsOnly?: boolean
  enabled?: boolean
}) {
  const queryParams: Record<string, string | number | boolean> = {}
  if (options?.contractTypeUid) queryParams.contract_type_uid = options.contractTypeUid
  if (options?.lightweight !== undefined) queryParams.lightweight = options.lightweight
  if (options?.status) queryParams.status = options.status
  if (options?.assetUid) queryParams.asset_uid = options.assetUid
  if (options?.assetIncludeChildren) queryParams.asset_include_children = 'true'
  if (options?.search) queryParams.q = options.search
  if (options?.include) queryParams.include = options.include.join(',')
  if (options?.headsOnly) queryParams.heads_only = 'true'

  const queryPath = '/api/v1/organizations/{orgCode}/contracts'
  const paginateParams = { path: { orgCode: org }, query: queryParams }
  const result = useQuery({
    queryKey: listQueryKey(queryPath, paginateParams),
    enabled: !!org && options?.enabled !== false,
    queryFn: () => fetchAllPages<Contract>(queryPath, paginateParams, CONTRACTS_PAGE_SIZE),
  })

  return {
    ...result,
    data: result.data ?? (EMPTY_DATA_ARRAY as unknown as Contract[]),
  }
}

// Contracts with stakeholders query hook - for asset contacts
export function useAssetContracts(org: string, assetUid: string, options?: { enabled?: boolean }) {
  const result = $api.useQuery('get', '/api/v1/organizations/{orgCode}/contracts', {
    params: {
      path: { orgCode: org },
      query: {
        asset_uid: assetUid,
        include: 'stakeholders,user,stakeholder_type' as any,
        limit: 100 // Get all contracts for this asset
      }
    }
  }, {
    enabled: options?.enabled !== false && !!org && !!assetUid
  })

  return {
    ...result,
    data: result.data as any
  }
}

// Contract query hook
export function useContract(org: string, contractUid: string, includes?: string[]) {
  const queryParams: Record<string, string> = {}
  if (includes && includes.length > 0) {
    queryParams.include = includes.join(',')
  }

  const result = $api.useQuery('get', '/api/v1/organizations/{orgCode}/contracts/{contractUid}', {
    params: {
      path: {
        orgCode: org,
        contractUid: contractUid
      },
      query: Object.keys(queryParams).length > 0 ? queryParams : undefined
    }
  }, {
    enabled: !!org && !!contractUid
  })

  return {
    ...result,
    data: extractItem(result) as Contract
  }
}

// Create contract mutation hook
export function useCreateContract(org: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation('post', '/api/v1/organizations/{orgCode}/contracts', {
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) => {
          const [method, path] = query.queryKey
          return method === 'get' && path === '/api/v1/organizations/{orgCode}/contracts'
        }
      })
    }
  })

  return {
    ...baseMutation,
    mutate: (data: CreateContractRequest, options?: any) => {
      baseMutation.mutate({
        params: { path: { orgCode: org } },
        body: data
      }, options)
    },
    mutateAsync: (data: CreateContractRequest, options?: any) => {
      return baseMutation.mutateAsync({
        params: { path: { orgCode: org } },
        body: data
      }, options)
    }
  }
}

// Update contract mutation hook
export function useUpdateContract(org: string, contractUid: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation('patch', '/api/v1/organizations/{orgCode}/contracts/{contractUid}', {
    onSuccess: () => {
      // Invalidate the specific contract query using the same format as the query
      queryClient.invalidateQueries({
        predicate: (query) => {
          const [method, path] = query.queryKey
          return method === 'get' && path === '/api/v1/organizations/{orgCode}/contracts/{contractUid}'
        }
      })
      // Invalidate the contracts list
      queryClient.invalidateQueries({
        predicate: (query) => {
          const [method, path] = query.queryKey
          return method === 'get' && path === '/api/v1/organizations/{orgCode}/contracts'
        }
      })
    }
  })

  return {
    ...baseMutation,
    mutate: (data: UpdateContractRequest, options?: any) => {
      baseMutation.mutate({
        params: { path: { orgCode: org, contractUid } },
        body: data
      }, options)
    },
    mutateAsync: (data: UpdateContractRequest, options?: any) => {
      return baseMutation.mutateAsync({
        params: { path: { orgCode: org, contractUid } },
        body: data
      }, options)
    }
  }
}

// Delete contract mutation hook
export function useDeleteContract(org: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation('delete', '/api/v1/organizations/{orgCode}/contracts/{contractUid}', {
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) => {
          const [method, path] = query.queryKey
          return method === 'get' && path === '/api/v1/organizations/{orgCode}/contracts'
        }
      })
    }
  })

  return {
    ...baseMutation,
    mutate: (contractUid: string, options?: any) => {
      baseMutation.mutate({
        params: { path: { orgCode: org, contractUid } }
      }, options)
    },
    mutateAsync: (contractUid: string, options?: any) => {
      return baseMutation.mutateAsync({
        params: { path: { orgCode: org, contractUid } }
      }, options)
    }
  }
}

// ---------------------------------------------------------------------------
// Contract clause properties (contract_data) — typed clause values, mirrored on
// the asset/document property endpoints. The Clauses UI reads which properties a
// contract type exposes via the junction, then the current values per contract.
// ---------------------------------------------------------------------------

// List the property definitions attached to a contract type (enriched junction:
// requirement, flags, display_group/order + embedded definition).
export function useContractTypeProperties(org: string, contractTypeUid: string, options?: { enabled?: boolean }) {
  const result = $api.useQuery('get', '/api/v1/organizations/{orgCode}/contract-types/{uid}/properties', {
    params: {
      path: { orgCode: org, uid: contractTypeUid }
    }
  }, {
    enabled: options?.enabled !== false && !!org && !!contractTypeUid
  })

  return {
    ...result,
    data: extractData(result) as unknown as ContractTypeProperty[]
  }
}

export type ExtractionReliability = components['schemas']['ExtractionReliability']
export type ExtractionReviewStat = components['schemas']['ExtractionReviewStat']

// The organization's own reviews of extracted clauses, per property, for one
// exact contract type (spec 2026-10-27-12 §C). Needs contracts.read.
export function useExtractionReviewStats(org: string, contractTypeCode: string | undefined, options?: { enabled?: boolean }) {
  const result = $api.useQuery('get', '/api/v1/organizations/{orgCode}/contracts/extraction-review-stats', {
    params: {
      path: { orgCode: org },
      query: { contract_type_code: contractTypeCode },
    }
  }, {
    enabled: options?.enabled !== false && !!org && !!contractTypeCode
  })

  return {
    ...result,
    data: (result.data?.data ?? []) as ExtractionReviewStat[]
  }
}

// List the current typed clause values for a contract (embeds property_definition).
export function useContractProperties(org: string, contractUid: string, options?: { enabled?: boolean }) {
  const result = $api.useQuery('get', '/api/v1/organizations/{orgCode}/contracts/{uid}/properties', {
    params: {
      path: { orgCode: org, uid: contractUid }
    }
  }, {
    enabled: options?.enabled !== false && !!org && !!contractUid
  })

  return {
    ...result,
    data: extractData(result) as unknown as ContractPropertyValue[]
  }
}

// refreshContractProperties invalidates the clause-values query for one contract.
// Follows the method+path+params predicate pattern (front/CLAUDE.md) so it only
// matches the contract we mutated, not every contract's properties.
export function refreshContractProperties(queryClient: QueryClient, org: string, contractUid: string) {
  queryClient.invalidateQueries({
    predicate: query => {
      const queryKey = query.queryKey as any[]
      if (queryKey.length >= 2 && queryKey[0] === 'get') {
        const path = queryKey[1]
        if (path === '/api/v1/organizations/{orgCode}/contracts/{uid}/properties') {
          const params = queryKey[2]?.params as { path?: { orgCode?: string; uid?: string } } | undefined
          if (params?.path?.orgCode === org && params?.path?.uid === contractUid) {
            return true
          }
        }
      }
      return false
    }
  })
}

// Re-read a contract's clauses from the document it was extracted from
// (spec 2026-09-16-03). Enqueues doc_entity_extraction and returns its job uid;
// the clause rows are rewritten asynchronously when the job runs, so the caller
// tells the user it is running rather than awaiting a new value.
export function useReextractContractClauses(org: string, contractUid: string) {
  const queryClient = useQueryClient()
  const baseMutation = $api.useMutation('post', '/api/v1/organizations/{orgCode}/contracts/{contractUid}/reextract-clauses', {
    onSuccess: () => {
      // The job has only been queued, so this refetch rarely shows new values
      // yet — it is there so a fast run is not hidden behind the 5-minute
      // stale time. The banner is what tells the user to come back.
      refreshContractProperties(queryClient, org, contractUid)
    }
  })

  return {
    ...baseMutation,
    mutate: (options?: any) => baseMutation.mutate({
      params: { path: { orgCode: org, contractUid } }
    }, options)
  }
}

// Upsert a clause value. Server-side this is the manual/confirm write path: it
// stamps the `manual` source and approved_by/approved_at (spec 2026-07-29-04).
export function useSetContractPropertyValue(org: string, contractUid: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation('put', '/api/v1/organizations/{orgCode}/contracts/{uid}/properties/{propertyUid}', {
    onSuccess: () => {
      refreshContractProperties(queryClient, org, contractUid)
      // The Improvement tab's verdicts/score are computed from these same
      // values — without this a saved clause edit leaves it showing the
      // pre-edit gap until an unrelated refetch happens to run.
      void queryClient.invalidateQueries({ queryKey: ['contract-clause-analysis', org, contractUid] })
      // A write over an extraction is a review: the review figures move.
      void queryClient.invalidateQueries({
        predicate: query => {
          const queryKey = query.queryKey as any[]
          return queryKey[0] === 'get' && queryKey[1] === '/api/v1/organizations/{orgCode}/contracts/extraction-review-stats'
        },
      })
    }
  })

  return {
    ...baseMutation,
    mutate: (propertyUid: string, value: unknown, options?: any) => {
      baseMutation.mutate({
        params: { path: { orgCode: org, uid: contractUid, propertyUid } },
        body: { value }
      }, options)
    },
    mutateAsync: (propertyUid: string, value: unknown, options?: any) => {
      return baseMutation.mutateAsync({
        params: { path: { orgCode: org, uid: contractUid, propertyUid } },
        body: { value }
      }, options)
    }
  }
}

// Remove a clause value.
export function useDeleteContractPropertyValue(org: string, contractUid: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation('delete', '/api/v1/organizations/{orgCode}/contracts/{uid}/properties/{propertyUid}', {
    onSuccess: () => {
      refreshContractProperties(queryClient, org, contractUid)
    }
  })

  return {
    ...baseMutation,
    mutate: (propertyUid: string, options?: any) => {
      baseMutation.mutate({
        params: { path: { orgCode: org, uid: contractUid, propertyUid } }
      }, options)
    },
    mutateAsync: (propertyUid: string, options?: any) => {
      return baseMutation.mutateAsync({
        params: { path: { orgCode: org, uid: contractUid, propertyUid } }
      }, options)
    }
  }
}

// Extra invalidation shared by the four contract-link mutations (add/remove
// stakeholder, add/remove asset). Their own sub-resource list
// (`.../stakeholders` or `.../assets`) is invalidated by each hook already;
// this additionally refreshes the contract DETAIL (ContractPage reads
// stakeholders/assets EMBEDDED via `?include=`, not from the sub-resource
// list) and the contracts LIST path — which `useAssetContracts` also keys
// on, so this one predicate covers AssetContracts.tsx too. Same predicate
// shape as `useUpdateContract`.
function invalidateContractDetailAndList(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({
    predicate: (query) => {
      const [method, path] = query.queryKey
      return method === 'get' && path === '/api/v1/organizations/{orgCode}/contracts/{contractUid}'
    }
  })
  queryClient.invalidateQueries({
    predicate: (query) => {
      const [method, path] = query.queryKey
      return method === 'get' && path === '/api/v1/organizations/{orgCode}/contracts'
    }
  })
  // A lease's guarantees card lists its GUARANTOR stakeholders (spec 2026-10-25-08).
  queryClient.invalidateQueries({
    predicate: (query) => {
      const [method, path] = query.queryKey
      return method === 'get' && path === '/api/v1/organizations/{orgCode}/contracts/{contractUid}/guarantees'
    }
  })
}

// Contract stakeholders query hook
export function useContractStakeholders(org: string, contractUid: string) {
  const result = $api.useQuery('get', '/api/v1/organizations/{orgCode}/contracts/{contractUid}/stakeholders', {
    params: {
      path: {
        orgCode: org,
        contractUid: contractUid
      }
    }
  }, {
    enabled: !!org && !!contractUid
  })

  return {
    ...result,
    data: extractData(result) as unknown as ContractStakeholder[]
  }
}

// Add contract stakeholder mutation hook
export function useAddContractStakeholder(org: string, contractUid: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation('post', '/api/v1/organizations/{orgCode}/contracts/{contractUid}/stakeholders', {
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) => {
          const [method, path] = query.queryKey
          return method === 'get' && path === '/api/v1/organizations/{orgCode}/contracts/{contractUid}/stakeholders'
        }
      })
      invalidateContractDetailAndList(queryClient)
    }
  })

  return {
    ...baseMutation,
    mutate: (data: CreateContractStakeholderRequest, options?: any) => {
      baseMutation.mutate({
        params: { path: { orgCode: org, contractUid } },
        body: data
      }, options)
    },
    mutateAsync: (data: CreateContractStakeholderRequest, options?: any) => {
      return baseMutation.mutateAsync({
        params: { path: { orgCode: org, contractUid } },
        body: data
      }, options)
    }
  }
}

// Update contract stakeholder mutation hook
export function useUpdateContractStakeholder(org: string, contractUid: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation('patch', '/api/v1/organizations/{orgCode}/contracts/{contractUid}/stakeholders/{stakeholderUid}', {
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) => {
          const [method, path] = query.queryKey
          return method === 'get' && path === '/api/v1/organizations/{orgCode}/contracts/{contractUid}/stakeholders'
        }
      })
    }
  })

  return {
    ...baseMutation,
    mutate: (stakeholderUid: string, data: UpdateContractStakeholderRequest, options?: any) => {
      baseMutation.mutate({
        params: { path: { orgCode: org, contractUid, stakeholderUid } },
        body: data
      }, options)
    },
    mutateAsync: (stakeholderUid: string, data: UpdateContractStakeholderRequest, options?: any) => {
      return baseMutation.mutateAsync({
        params: { path: { orgCode: org, contractUid, stakeholderUid } },
        body: data
      }, options)
    }
  }
}

// Remove contract stakeholder mutation hook
export function useRemoveContractStakeholder(org: string, contractUid: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation('delete', '/api/v1/organizations/{orgCode}/contracts/{contractUid}/stakeholders/{stakeholderUid}', {
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) => {
          const [method, path] = query.queryKey
          return method === 'get' && path === '/api/v1/organizations/{orgCode}/contracts/{contractUid}/stakeholders'
        }
      })
      invalidateContractDetailAndList(queryClient)
    }
  })

  return {
    ...baseMutation,
    mutate: (stakeholderUid: string, options?: any) => {
      baseMutation.mutate({
        params: { path: { orgCode: org, contractUid, stakeholderUid } }
      }, options)
    },
    mutateAsync: (stakeholderUid: string, options?: any) => {
      return baseMutation.mutateAsync({
        params: { path: { orgCode: org, contractUid, stakeholderUid } }
      }, options)
    }
  }
}

// Contract assets query hook
export function useContractAssets(org: string, contractUid: string) {
  const result = $api.useQuery('get', '/api/v1/organizations/{orgCode}/contracts/{contractUid}/assets', {
    params: {
      path: {
        orgCode: org,
        contractUid: contractUid
      }
    }
  }, {
    enabled: !!org && !!contractUid
  })

  return {
    ...result,
    data: extractData(result) as unknown as ContractAsset[]
  }
}

// Add contract asset mutation hook
export function useAddContractAsset(org: string, contractUid: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation('post', '/api/v1/organizations/{orgCode}/contracts/{contractUid}/assets', {
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) => {
          const [method, path] = query.queryKey
          return method === 'get' && path === '/api/v1/organizations/{orgCode}/contracts/{contractUid}/assets'
        }
      })
      invalidateContractDetailAndList(queryClient)
    }
  })

  return {
    ...baseMutation,
    mutate: (data: CreateContractAssetRequest, options?: any) => {
      baseMutation.mutate({
        params: { path: { orgCode: org, contractUid } },
        body: data
      }, options)
    },
    mutateAsync: (data: CreateContractAssetRequest, options?: any) => {
      return baseMutation.mutateAsync({
        params: { path: { orgCode: org, contractUid } },
        body: data
      }, options)
    }
  }
}

// Remove contract asset mutation hook
export function useRemoveContractAsset(org: string, contractUid: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation('delete', '/api/v1/organizations/{orgCode}/contracts/{contractUid}/assets/{assetUid}', {
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) => {
          const [method, path] = query.queryKey
          return method === 'get' && path === '/api/v1/organizations/{orgCode}/contracts/{contractUid}/assets'
        }
      })
      invalidateContractDetailAndList(queryClient)
    }
  })

  return {
    ...baseMutation,
    mutate: (assetUid: string, options?: any) => {
      baseMutation.mutate({
        params: { path: { orgCode: org, contractUid, assetUid } }
      }, options)
    },
    mutateAsync: (assetUid: string, options?: any) => {
      return baseMutation.mutateAsync({
        params: { path: { orgCode: org, contractUid, assetUid } }
      }, options)
    }
  }
}

// --- Admin organizations (platform-admin only) ------------------------------

export type AdminOrganization = components['schemas']['AdminOrganization']

export interface AdminOrgFilters {
  q?: string
  status?: 'active' | 'suspended' | 'deleted' | 'template' | 'all'
  page?: number
  limit?: number
}

// Cache-invalidation predicate shared by all admin mutations: invalidates
// every admin organizations query regardless of filter combination.
function invalidateAdminOrgs(queryClient: QueryClient) {
  queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey as any[]
      return Array.isArray(key) && key.length >= 2 && key[0] === 'get' &&
        (key[1] === '/api/v1/admin/organizations' || key[1] === '/api/v1/admin/organizations/{orgUid}')
    },
  })
}

export function useAdminOrganizations(filters: AdminOrgFilters) {
  const result = $api.useQuery('get', '/api/v1/admin/organizations', {
    params: {
      query: {
        q: filters.q,
        status: filters.status,
        page: filters.page,
        limit: filters.limit,
      } as any,
    },
  })
  return result
}

export function useAdminOrganization(orgUid: string | undefined) {
  const result = $api.useQuery('get', '/api/v1/admin/organizations/{orgUid}', {
    params: { path: { orgUid: orgUid ?? '' } },
  }, { enabled: Boolean(orgUid) })
  return result
}

export function useSuspendAdminOrganization() {
  const queryClient = useQueryClient()
  return $api.useMutation('post', '/api/v1/admin/organizations/{orgUid}/suspend', {
    onSuccess: () => invalidateAdminOrgs(queryClient),
  })
}

export function useUnsuspendAdminOrganization() {
  const queryClient = useQueryClient()
  return $api.useMutation('post', '/api/v1/admin/organizations/{orgUid}/unsuspend', {
    onSuccess: () => invalidateAdminOrgs(queryClient),
  })
}

export function useDeleteAdminOrganization() {
  const queryClient = useQueryClient()
  return $api.useMutation('delete', '/api/v1/admin/organizations/{orgUid}', {
    onSuccess: () => invalidateAdminOrgs(queryClient),
  })
}

export function useRestoreAdminOrganization() {
  const queryClient = useQueryClient()
  return $api.useMutation('post', '/api/v1/admin/organizations/{orgUid}/restore', {
    onSuccess: () => invalidateAdminOrgs(queryClient),
  })
}

// --- Unified organizations hooks --------------------------------------------
// One shape that covers both the admin and non-admin endpoints. Admin-only
// fields (status, counts, last_active_at, suspended_at, deleted_at) are
// undefined for rows that came from the non-admin endpoint.

export type OrgStatus = 'active' | 'suspended' | 'deleted' | 'template'

export interface NormalizedOrg {
  uid: string
  code: string
  name: string
  is_template: boolean
  created_at?: string
  updated_at?: string
  status?: OrgStatus
  suspended_at?: string | null
  deleted_at?: string | null
  last_active_at?: string | null
  counts?: {
    members?: number
    assets?: number
    documents?: number
    issues?: number
  }
  picture?: { document_uid?: string } | null
}

export interface UnifiedOrgFilters {
  q?: string
  status?: OrgStatus | 'all'
  is_template?: boolean
  page?: number
  limit?: number
}

function adminToNormalized(o: AdminOrganization): NormalizedOrg {
  return {
    uid: o.uid,
    code: o.code,
    name: o.name,
    is_template: !!o.is_template,
    created_at: o.created_at,
    updated_at: o.updated_at,
    status: o.status as OrgStatus,
    suspended_at: o.suspended_at ?? null,
    deleted_at: o.deleted_at ?? null,
    last_active_at: o.last_active_at ?? null,
    counts: o.counts,
  }
}

function regularToNormalized(o: any): NormalizedOrg {
  return {
    uid: o.uid,
    code: o.code,
    name: o.name,
    is_template: !!o.is_template,
    created_at: o.created_at,
    updated_at: o.updated_at,
    picture: o.picture,
  }
}

export function useOrganizationListUnified(filters: UnifiedOrgFilters, isAdmin: boolean) {
  const adminResult = $api.useQuery('get', '/api/v1/admin/organizations', {
    params: {
      query: {
        q: filters.q,
        status: filters.status,
        page: filters.page,
        limit: filters.limit,
      } as any,
    },
  }, { enabled: isAdmin })

  const regularResult = $api.useQuery('get', '/api/v1/organizations', {
    params: {
      query: {
        is_template: filters.is_template,
        q: filters.q,
        page: filters.page,
        limit: filters.limit,
      } as any,
    },
  }, { enabled: !isAdmin })

  const active = isAdmin ? adminResult : regularResult
  const adminEntries: AdminOrganization[] = ((adminResult.data as any)?.data ?? []) as AdminOrganization[]
  const regularEntries: any[] = ((regularResult.data as any)?.data ?? (regularResult.data as any) ?? []) as any[]
  const rows: NormalizedOrg[] = isAdmin
    ? adminEntries.map(adminToNormalized)
    : regularEntries.map(regularToNormalized)

  return {
    rows,
    isLoading: active.isLoading,
    error: active.error,
    refetch: active.refetch,
  }
}

// Looks up a single organization. Always uses the non-admin endpoint (which
// accepts a code), and additionally pulls admin extras when the caller is a
// platform admin. The drawer URL uses `?org=<code>` to keep the param
// human-friendly.
export function useOrganizationDetailUnified(
  orgCode: string | undefined,
  isAdmin: boolean,
) {
  const baseQuery = $api.useQuery('get', '/api/v1/organizations/{orgCode}', {
    params: {
      path: { orgCode: orgCode ?? '' },
      query: { include: 'picture' } as any,
    },
  }, { enabled: Boolean(orgCode) })

  const baseOrg = baseQuery.data as any
  const adminUid: string | undefined = isAdmin && baseOrg?.uid ? baseOrg.uid : undefined

  const adminQuery = $api.useQuery('get', '/api/v1/admin/organizations/{orgUid}', {
    params: { path: { orgUid: adminUid ?? '' } },
  }, { enabled: Boolean(adminUid) })

  const adminOrg = adminQuery.data as AdminOrganization | undefined
  const merged: NormalizedOrg | undefined = baseOrg
    ? {
        ...regularToNormalized(baseOrg),
        ...(adminOrg ? adminToNormalized(adminOrg) : {}),
        picture: baseOrg.picture,
      }
    : undefined

  return {
    org: merged,
    isLoading: baseQuery.isLoading || (Boolean(adminUid) && adminQuery.isLoading),
    error: baseQuery.error || adminQuery.error,
    refetch: () => {
      baseQuery.refetch()
      if (adminUid) adminQuery.refetch()
    },
  }
}
/**
 * Folder hierarchy applying to an asset (spec 2026-07-27-06).
 *
 * The backend resolves the layout through the asset's site ancestor and
 * returns, per folder, the document types that map to it plus the number of
 * the asset's documents falling in it. When the asset resolves to no layout,
 * `layout` is null and `folders` is empty — callers then keep their flat list.
 */
export function useAssetDocumentFolders(org: string, assetUid?: string) {
  const result = $api.useQuery('get', '/api/v1/organizations/{orgCode}/assets/{assetUid}/document-folders', {
    params: {
      path: { orgCode: org, assetUid: assetUid ?? '' }
    }
  }, { enabled: Boolean(org) && Boolean(assetUid) })

  return {
    ...result,
    data: result.data as AssetDocumentFolders | undefined
  }
}

/**
 * Invalidate the folder tree resolved for an asset.
 *
 * The auto-generated openapi-react-query keys are
 * `['get', '<path template>', { params: { path: {...} } }]`, so the naive
 * two-element destructure never matches — the predicate has to check the
 * method, the path template *and* the params (see front/CLAUDE.md).
 */
export function refreshAssetDocumentFolders(queryClient: QueryClient, org: string, assetUid: string) {
  queryClient.invalidateQueries({
    predicate: (query) => {
      const queryKey = query.queryKey as any[]
      if (queryKey.length < 2 || queryKey[0] !== 'get') return false
      if (queryKey[1] !== '/api/v1/organizations/{orgCode}/assets/{assetUid}/document-folders') return false
      const params = queryKey.find(item => item && typeof item === 'object' && 'params' in item)?.params
      return params?.path?.orgCode === org && params?.path?.assetUid === assetUid
    }
  })
}

export type ContractDocumentFolders = components['schemas']['ContractDocumentFolders']

/**
 * Folder hierarchy applying to a contract (spec 2026-08-13-01 §4) — the
 * contract twin of `useAssetDocumentFolders`.
 *
 * The backend resolves the layout through the contract's own column, then its
 * contract type family, then the organization's default *contract* layout, and
 * returns per folder the document types mapping to it plus the number of the
 * contract's documents (documents.contract_id) falling in it. When nothing
 * resolves, `layout` is null and `folders` is empty — callers then keep their
 * flat list.
 */
export function useContractDocumentFolders(org: string, contractUid?: string, enabled = true) {
  const result = $api.useQuery('get', '/api/v1/organizations/{orgCode}/contracts/{contractUid}/document-folders', {
    params: {
      path: { orgCode: org, contractUid: contractUid ?? '' }
    }
  }, { enabled: Boolean(org) && Boolean(contractUid) && enabled })

  return {
    ...result,
    data: result.data as ContractDocumentFolders | undefined
  }
}

/** Invalidate the folder tree resolved for a contract (see the asset twin). */
export function refreshContractDocumentFolders(queryClient: QueryClient, org: string, contractUid: string) {
  queryClient.invalidateQueries({
    predicate: (query) => {
      const queryKey = query.queryKey as any[]
      if (queryKey.length < 2 || queryKey[0] !== 'get') return false
      if (queryKey[1] !== '/api/v1/organizations/{orgCode}/contracts/{contractUid}/document-folders') return false
      const params = queryKey.find(item => item && typeof item === 'object' && 'params' in item)?.params
      return params?.path?.orgCode === org && params?.path?.contractUid === contractUid
    }
  })
}

export type DocumentFolderLayout = components['schemas']['DocumentFolderLayout']

/**
 * The organization's document folder layouts (the ontology-level definitions
 * managed on /$org/folder-layouts). Used by the asset documents tab to let a
 * site pick which one structures its documents.
 */
export function useFolderLayouts(org: string, enabled = true, targetEntityType?: DocumentFolderLayout['target_entity_type']) {
  // ?target_entity_type= restricts the list to layouts that can actually be
  // attached to the holder asking (spec 2026-08-13-01 §2): the asset picker
  // asks for `asset` layouts, the contract picker for `contract` ones.
  const query = targetEntityType ? { target_entity_type: targetEntityType } : undefined
  const result = $api.useQuery('get', '/api/v1/organizations/{orgCode}/document-folders-layouts', {
    params: { path: { orgCode: org }, query }
  }, { enabled: Boolean(org) && enabled })

  return {
    ...result,
    data: ((result.data as { layouts?: DocumentFolderLayout[] } | undefined)?.layouts ?? []) as DocumentFolderLayout[]
  }
}

// The commercial lease calendar of a contract (spec 2026-10-25-14). It is
// computed from the clauses, so it is never served from a stale cache: a clause
// edited on the Clauses tab moves the dates the next time the card mounts.
export function useLeaseCalendar(org: string, contractUid: string | undefined) {
  return $api.useQuery(
    'get',
    '/api/v1/organizations/{orgCode}/contracts/{contractUid}/lease-calendar',
    { params: { path: { orgCode: org, contractUid: contractUid ?? '' } } },
    { enabled: !!contractUid, staleTime: 0 },
  )
}
